/**
 * NOCTURNE THEME — theme.js
 * Vanilla JS — no jQuery, no external dependencies
 */

'use strict';

/* ============================================================
   UTILITY FUNCTIONS
   ============================================================ */

function formatMoney(cents) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const value = (cents / 100).toFixed(2);
  return '$' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/* Update every free-shipping progress bar on the page (cart page + cart drawer).
   Each bar carries its own data-threshold (cents); we recompute fill + message. */
function updateFreeShipBars(totalCents) {
  document.querySelectorAll('[data-cart-free-ship]').forEach(bar => {
    const threshold = parseInt(bar.dataset.threshold, 10);
    if (!threshold || threshold <= 0) return;
    bar.dataset.current = totalCents;
    const fill = bar.querySelector('[data-fs-fill]');
    const msg = bar.querySelector('[data-fs-msg]');
    const pct = Math.min(100, Math.round((totalCents / threshold) * 100));
    if (fill) fill.style.width = pct + '%';
    const remaining = threshold - totalCents;
    if (remaining > 0) {
      bar.classList.remove('is-unlocked');
      if (msg) {
        msg.innerHTML = 'Spend <strong>' + formatMoney(remaining) +
          '</strong> more to unlock <strong>free shipping</strong>';
      }
    } else {
      bar.classList.add('is-unlocked');
      if (msg) msg.innerHTML = '🦇 You\'ve unlocked <strong>free shipping</strong>!';
    }
  });
}

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

// Non-blocking replacement for alert() — a native alert() freezes the page's
// JS thread until dismissed, which is exactly what made the cart's stock-cap
// messaging feel broken (a shopper's click looks like it silently did nothing
// while the modal sits off-screen or behind a slow repaint). One shared toast
// element, re-triggered per call; auto-dismisses so it can never block input.
//
// Placement is decided at call time, not hardcoded per call site: if the cart
// drawer is open, the toast is parented INSIDE it (position:absolute, centered
// over the drawer panel) since that's what the shopper is looking at; otherwise
// it's parented on <body> and centered over the whole viewport. This also
// covers the /cart page correctly — it never has the drawer open, so it always
// gets the whole-site-centered placement.
let _toastEl = null;
let _toastTextEl = null;
let _toastTimer = null;
function showToast(message) {
  if (!_toastEl) {
    _toastEl = document.createElement('div');
    _toastEl.className = 'theme-toast';
    _toastEl.setAttribute('role', 'status');
    _toastEl.setAttribute('aria-live', 'polite');
    // Skull + exclamation badge — a bare skull reads as brand decoration on this
    // storefront (skull art is everywhere), not a warning; the badge disambiguates
    // it as "alert" while keeping the gothic identity. One inline SVG, not two
    // elements — the badge is just more shapes in the same viewBox.
    _toastEl.innerHTML =
      '<svg class="theme-toast__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M12 2C7.03 2 3 6.03 3 11c0 3.11 1.64 5.4 3 6.9V21a1 1 0 0 0 1 1h1.5v-2h1v2h3v-2h1v2H16a1 1 0 0 0 1-1v-3.1c1.36-1.5 3-3.79 3-6.9 0-4.97-4.03-9-9-9z"/>' +
      '<circle cx="8.7" cy="11" r="1.5" fill="currentColor" stroke="none"/>' +
      '<circle cx="15.3" cy="11" r="1.5" fill="currentColor" stroke="none"/>' +
      '<path d="M12 12.5l-1.1 2.2h2.2z" fill="currentColor" stroke="none"/>' +
      '<circle cx="18" cy="18" r="4.4" style="fill:var(--color-bg-2, #1a1420);" stroke="none"/>' +
      '<line x1="18" y1="15.3" x2="18" y2="17.9" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="18" cy="19.7" r="0.75" fill="currentColor" stroke="none"/></svg>' +
      '<span class="theme-toast__text"></span>';
    _toastTextEl = _toastEl.querySelector('.theme-toast__text');
  }
  const drawer = document.querySelector('.cart-drawer.is-open');
  if (drawer) {
    _toastEl.classList.add('theme-toast--drawer');
    if (_toastEl.parentElement !== drawer) drawer.appendChild(_toastEl);
  } else {
    _toastEl.classList.remove('theme-toast--drawer');
    if (_toastEl.parentElement !== document.body) document.body.appendChild(_toastEl);
  }
  _toastTextEl.textContent = message;
  _toastEl.classList.add('is-visible');
  clearTimeout(_toastTimer);
  _toastTimer = setTimeout(() => _toastEl.classList.remove('is-visible'), 2200);
}

/* ============================================================
   FREE PIN BACK — auto-add a free locking back, one per enamel pin.
   Config (variant id + handle) is emitted as window.FreePinBack by
   snippets/cart-drawer.liquid. Pins are flagged at add-time with a hidden
   `_pin` line-item property (the product form stamps it in Liquid; the
   cart-rec and quick-add paths stamp it here) because /cart.js exposes no
   tags and pins have a blank product_type. The "Free Locking Pin Back with
   Any Pin" automatic discount zeroes the $1 line at checkout; this code only
   manages its QUANTITY, inventory-aware so a sold-out freebie can never block
   a pin checkout.
   ============================================================ */

const FREE_BACK_PIN_TAGS = ['enamel pin', 'enamel pins', 'enamel-pin', 'pin', 'pins', 'hard enamel', 'soft enamel', 'deal pin', 'deal pins'];

function freeBackConfig() {
  const c = window.FreePinBack;
  return (c && c.variantId != null) ? c : null;
}

// Only the THEME-managed freebie line (marked `_freebie`) is locked/managed. A
// standalone $1 purchase of the same back (no `_freebie`) is a normal line.
function isManagedFreeBack(item) {
  return !!(item.properties && (item.properties._freebie === '1' || item.properties._freebie === 1));
}

function isFreeBackLine(item) {
  const c = freeBackConfig();
  return !!c && String(item.variant_id) === String(c.variantId) && isManagedFreeBack(item);
}

// True for a real pin. Used on the JS add paths (cart-rec, quick-add) that hold
// a full product object; the product-page form decides this in Liquid instead.
function isPinProduct(product) {
  if (!product) return false;
  const type = String(product.type || product.product_type || '').toLowerCase();
  if (type.indexOf('pin') !== -1 || type.indexOf('enamel') !== -1) return true;
  const tags = (product.tags || []).map(t => String(t).toLowerCase().trim());
  return FREE_BACK_PIN_TAGS.some(t => tags.indexOf(t) !== -1);
}

async function fetchCartJson() {
  // Retry on 429 (Shopify throttles the cart API under bursts of requests).
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const r = await fetch('/cart.js');
      if (r.ok) return await r.json();
      if (r.status === 429) { await new Promise(res => setTimeout(res, 500 * (attempt + 1))); continue; }
      return null;
    } catch (e) { return null; }
  }
  return null;
}

// Serial mutex for ALL cart-mutating requests. Every add / quantity change /
// remove / free-back reconcile runs through this one promise chain, so no two
// cart writes are ever in flight at the same time. This kills two failure modes
// that were corrupting the free pin-back count: (1) concurrent read-modify-write
// passes racing each other on the server, and (2) a burst of parallel requests
// tripping Shopify's 429 rate limiter, which silently dropped the reconcile's
// adjustment and stranded a charged/mismatched back line. Serialized, requests
// stay ordered and paced. `fn` must NOT itself call withCartLock (no nesting) —
// callers chain locked ops with sequential awaits instead.
let _cartLock = Promise.resolve();
function withCartLock(fn) {
  const result = _cartLock.then(fn, fn);
  _cartLock = result.then(() => {}, () => {});
  return result;
}

// ---- Cart "updating" busy state --------------------------------------------
// Ref-counted so back-to-back / nested cart writes keep it on until the last one
// settles. While busy, `html.cart-busy` is set: the CSS shows the in-cart
// "Updating cart…" veil (.cart-updating-veil — baked into the drawer + cart-page
// templates, so it survives item repaints) and DISABLES the +/- steppers. The
// stepper handlers also hard-return on isCartBusy(). Together that stops a rapid
// burst of clicks from queueing overlapping quantity writes — those overlapping
// writes were what briefly desynced the free-back count mid-flight before the
// cart re-settled a few seconds later.
let _cartBusyCount = 0;
function isCartBusy() { return _cartBusyCount > 0; }
function setCartUpdating(on) {
  _cartBusyCount = Math.max(0, _cartBusyCount + (on ? 1 : -1));
  document.documentElement.classList.toggle('cart-busy', _cartBusyCount > 0);
}

// POST a cart mutation; returns true on success. Retries once on 429 so a single
// throttled write doesn't silently leave the cart in a half-updated state.
async function postCart(url, body) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) return true;
      if (res.status === 429 && attempt === 0) { await new Promise(r => setTimeout(r, 600)); continue; }
      return false;
    } catch (e) { return false; }
  }
  return false;
}

// Live stock of the freebie (tracked, deny-oversell). Returns how many more we
// may add so an add never exceeds stock (which would 422 and could block the
// pin checkout).
async function freeBackAvailability() {
  const c = freeBackConfig();
  if (!c) return { available: false, qty: 0 };
  try {
    const r = await fetch('/products/' + c.handle + '.js');
    if (!r.ok) return { available: false, qty: 0 };
    const p = await r.json();
    const v = (p.variants || []).find(x => String(x.id) === String(c.variantId)) || (p.variants || [])[0];
    if (!v) return { available: false, qty: 0 };
    const qty = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity : null;
    return { available: !!v.available, qty: qty };
  } catch (e) { return { available: false, qty: null }; }
}

// Stock cap for a cart line's variant, or null if untracked / not deny-oversell
// (no cap). Reads the line's own `_stockcap` property first — stamped at add
// time (see CartDrawer.addToCart + CardQuickAdd.addVariant) from Liquid-rendered
// inventory data, so it's correct regardless of what page the drawer is later
// opened from. Falls back to window.productInventory (only populated on product
// pages). NOTE: there is deliberately no live-fetch fallback here — Shopify's
// public /products/{handle}.js endpoint does not expose inventory_policy or
// inventory_quantity per variant (confirmed empirically), so it can never
// answer this question; a property stamped at add time is the only reliable source.
function variantStockCap(item) {
  if (!item) return null;
  if (item.properties && item.properties._stockcap != null && item.properties._stockcap !== '') {
    const cap = parseInt(item.properties._stockcap, 10);
    if (!isNaN(cap)) return cap;
  }
  const inv = window.productInventory || {};
  const fast = inv[String(item.variant_id)];
  return (fast != null) ? fast : null;
}

// True when a cart line is a deal-eligible pin that should draw a free back.
// Primary signal is the `_pin` property the product form / quick-add stamp in
// Liquid. FALLBACK: a pin that entered by a non-stamping path — a raw /cart/add.js
// call, a third-party app, or a cart created before `_pin` shipped — still carries
// its `product_type`, so recognise it by that too (mirrors the Liquid detection:
// type contains "pin"/"enamel"). Without this fallback an unstamped pin line is
// invisible to reconcileFreeBacks FOREVER: Shopify's server discount still applies
// the 4-for-$20 deal, but the shopper is silently shorted their free backs and the
// count can never self-heal. Never treats the free-back variant itself as a pin.
function isPinLine(item, backVariantId) {
  if (!item) return false;
  if (backVariantId != null && String(item.variant_id) === String(backVariantId)) return false;
  if (item.properties && (item.properties._pin === '1' || item.properties._pin === 1)) return true;
  const t = (item.product_type || '').toLowerCase();
  return t.indexOf('pin') !== -1 || t.indexOf('enamel') !== -1;
}

// Keep the free-back quantity equal to the pin count (one per pin). Returns
// { changed, cart }. Runs through withCartLock so its read-plan-write is one
// atomic critical section — no pin-quantity write can slip in mid-reconcile, and
// two reconciles (drawer + cart page, both present on every page) can never
// overlap. Reads the cart FRESH inside the lock every time, so it always
// reconciles against the current pin count, and rewrites the managed back lines
// to exactly that count in ONE atomic request.
async function reconcileFreeBacks() {
  return withCartLock(async () => {
    const c = freeBackConfig();
    // Always read fresh inside the lock so we never plan against a stale snapshot
    // that a just-completed pin write already changed.
    let cart = await fetchCartJson();
    if (!c || !cart || !cart.items) return { changed: false, cart: cart };

    let pinQty = 0;
    const backLines = [];                                 // ALL theme-managed freebie lines
    cart.items.forEach(i => {
      if (String(i.variant_id) === String(c.variantId) && isManagedFreeBack(i)) {
        backLines.push(i);
      } else if (isPinLine(i, c.variantId)) {
        pinQty += i.quantity;                             // a standalone $1 back is neither
      }
    });

    const haveTotal = backLines.reduce((n, l) => n + l.quantity, 0);
    let desired = pinQty;                                 // one free back per pin

    // Inventory guard only matters when we would ADD beyond what we already have.
    if (desired > haveTotal) {
      const avail = await freeBackAvailability();
      if (!avail.available) desired = haveTotal;          // sold out -> leave as-is
      else if (avail.qty != null) desired = Math.min(desired, haveTotal + Math.max(0, avail.qty));
    }

    if (desired === haveTotal) return { changed: false, cart: cart };

    // Collapse to ONE managed line at `desired`, removing any extras. Shopify's
    // buy-a-pin-get-a-back automatic discount SPLITS the freebie line when only part
    // of it is covered — e.g. drop from 5 pins to 4 and the qty-5 back line becomes a
    // free qty-4 line PLUS a CHARGED qty-1 line. Accounting for EVERY managed back
    // line and rewriting them in a SINGLE /cart/update.js (survivor -> desired, the
    // rest -> 0) means the sweep is one atomic server operation — no window for the
    // discount engine to re-split between separate change requests, and one request
    // instead of N (far less likely to hit the rate limiter).
    let ok = false;
    if (backLines.length === 0) {
      // No managed line exists yet — must ADD (only /cart/add.js can set the
      // _freebie line property). Skips when desired is 0 (nothing to do).
      ok = desired > 0
        ? await postCart('/cart/add.js', { id: c.variantId, quantity: desired, properties: { _freebie: '1' } })
        : true;
    } else {
      backLines.sort((a, b) => b.quantity - a.quantity); // largest line is the survivor
      const updates = { [backLines[0].key]: desired };
      for (let k = 1; k < backLines.length; k++) updates[backLines[k].key] = 0;
      ok = await postCart('/cart/update.js', { updates });
    }

    const fresh = await fetchCartJson();
    return { changed: ok, cart: fresh || cart };
  });
}

/* ============================================================
   1. CART DRAWER
   ============================================================ */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-overlay]');
    this.itemsContainer = document.querySelector('[data-cart-items]');
    this.subtotalEl = document.querySelector('[data-cart-subtotal]');
    this.savingsRowEl = document.querySelector('[data-cart-savings-row]');
    this.savingsEl = document.querySelector('[data-cart-savings]');
    this.totalEl = document.querySelector('[data-cart-total]');
    this.freeShipBar = document.querySelector('.cart-free-ship--drawer');
    this.cartCountEls = document.querySelectorAll('[data-cart-count]');
    this.dealReminderEl = document.querySelector('[data-cart-deal-reminder]');

    // Bundle-progress reminder rules (same JSON island promo-banners.liquid feeds
    // recCard). Only the quantity-break pin deal has min > 0 today; if a second
    // dynamic-min rule is ever added, this picks the first one only.
    this._dealRule = null;
    try {
      const rulesEl = document.getElementById('PromoBannerRules');
      const rules = rulesEl ? (JSON.parse(rulesEl.textContent) || []) : [];
      this._dealRule = rules.find(r => r.min > 0) || null;
    } catch (e) { this._dealRule = null; }

    if (!this.drawer) return;

    this.bindEvents();
  }

  openDrawer() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.classList.add('no-scroll');
    this.renderCartItems();
  }

  // Open the drawer INSTANTLY with a loading state, called at click time BEFORE the
  // add + cart-fetch round-trips, so a tap feels immediate instead of waiting a full
  // network hop for the drawer to appear. renderCartItems() (via openDrawer(), run the
  // moment the add lands) swaps in the real cart. Only paints the skeleton when opening
  // fresh — if the drawer is already open (adding another item), the visible lines stay
  // put and simply repaint when the new cart arrives.
  openDrawerLoading() {
    if (!this.drawer) return;
    const wasOpen = this.drawer.classList.contains('is-open');
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.classList.add('no-scroll');
    if (!wasOpen && this.itemsContainer) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__loading" role="status">
          <span class="cart-drawer__spinner" aria-hidden="true"></span>
          <p>Summoning your cart&hellip;</p>
        </div>`;
    }
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  }

  async fetchCart() {
    // Delegate to the shared 429-resilient reader (retries when Shopify throttles).
    return fetchCartJson();
  }

  async addToCart(formData) {
    try {
      // Inventory guard: for tracked, deny-overselling variants, don't let the
      // cart exceed available stock — account for units already in the cart so a
      // second add can't push past the limit (window.productInventory only lists
      // variants that track quantity and deny overselling).
      const variantId = formData.get('id');
      const reqQty = parseInt(formData.get('quantity'), 10) || 1;
      const inv = window.productInventory || {};
      const knownStock = (variantId != null && inv[String(variantId)] != null) ? inv[String(variantId)] : null;
      if (knownStock != null) {
        const stock = knownStock;
        let inCart = 0;
        const cart = await this.fetchCart();
        if (cart && cart.items) {
          cart.items.forEach(i => {
            if (String(i.variant_id) === String(variantId)) inCart += i.quantity;
          });
        }
        const remaining = stock - inCart;
        if (reqQty > remaining) {
          showToast(
            remaining <= 0
              ? (stock <= 0 ? 'Out of stock.' : 'Max stock already added.')
              : `Only ${remaining} more available.`
          );
          return null;
        }
      }

      // Inventory guard passed — open the drawer instantly (loading state) so the
      // click feels immediate, before the add + cart-fetch round-trips resolve.
      this.openDrawerLoading();

      // Stamp the known stock cap onto the line itself so the drawer's quantity
      // stepper can keep enforcing it later even if reopened from a page that
      // never had window.productInventory populated (see variantStockCap()).
      if (knownStock != null) formData.set('properties[_stockcap]', String(knownStock));

      // Serialized through the cart lock so the add can't overlap an in-flight
      // free-back reconcile (which would race on the back count).
      const response = await withCartLock(() => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString()
      }));
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || 'Could not add to cart');
      }
      const item = await response.json();
      // openDrawer() opens instantly and renders once. (It used to render here AND
      // again in openDrawer — a double render that also delayed the drawer opening
      // until the first full render + free-back reconcile finished.)
      this.openDrawer();
      return item;
    } catch (err) {
      console.error('[CartDrawer] addToCart error:', err);
      showToast(err.message || "Couldn't add to cart.");
      // The add failed after we optimistically opened the loading drawer — repaint the
      // true (unchanged) cart so the spinner doesn't hang.
      this.renderCartItems();
      return null;
    }
  }

  // Set a visual line's TOTAL quantity across however many real cart lines it
  // currently spans (usually 1, but a discount Function can split one purchase
  // into several). Keeps the largest existing line as the survivor and zeroes
  // the rest — same "collapse to one line" pattern reconcileFreeBacks() already
  // uses for the free pin-back, applied generically to any grouped row.
  async updateGroupQuantity(items, quantity, inputEl) {
    if (!items || !items.length) return;
    setCartUpdating(true); // veil on + steppers disabled for the whole write→reconcile→repaint chain
    if (this.itemsContainer) {
      this.itemsContainer.classList.add('is-loading');
    }
    try {
      let targetQty = Math.max(0, parseInt(quantity, 10) || 0);
      const sorted = items.slice().sort((a, b) => b.quantity - a.quantity);
      const haveTotal = sorted.reduce((n, l) => n + l.quantity, 0);

      // Cap to live stock so the stepper can never push a tracked, deny-oversell
      // variant past what's actually in stock. The initial add-to-cart click has
      // its own guard (window.productInventory, product-page only) — this stepper
      // acts on quantity already IN the cart and never had a cap of its own.
      if (targetQty > haveTotal) {
        const cap = await variantStockCap(items[0]);
        if (cap != null && targetQty > cap) {
          targetQty = cap;
          showToast(cap > haveTotal
            ? `Only ${cap} available in stock.`
            : 'Max stock already added.');
        }
      }
      if (inputEl) inputEl.value = targetQty;
      if (targetQty === haveTotal) return; // clamped back to current — nothing to write

      // Optimistic free-ship bump: nudge the progress bar right now using the
      // line's own known price, instead of waiting for the write + refetch +
      // reconcile chain that follows (each its own network round-trip, easily
      // 1s+ combined) — that wait was what made the bar feel slow to update.
      // The real fetch below still corrects this to the authoritative total.
      const unitPrice = sorted[0] && typeof sorted[0].price === 'number' ? sorted[0].price : 0;
      if (unitPrice) {
        const bar = document.querySelector('[data-cart-free-ship]');
        if (bar) {
          const current = parseInt(bar.dataset.current, 10) || 0;
          updateFreeShipBars(current + (targetQty - haveTotal) * unitPrice);
        }
      }

      const survivor = sorted[0];
      const updates = { [survivor.key]: targetQty };
      sorted.slice(1).forEach(i => { updates[i.key] = 0; });
      // Serialized so this pin-quantity write can never overlap a free-back
      // reconcile (or another quantity write) — that overlap was corrupting the
      // back count. renderCartItems() then reconciles as a separate locked step.
      const ok = await withCartLock(() => postCart('/cart/update.js', { updates }));
      if (!ok) throw new Error('Cart update failed');
      await this.renderCartItems();
    } catch (err) {
      console.error('[CartDrawer] updateGroupQuantity error:', err);
    } finally {
      if (this.itemsContainer) {
        this.itemsContainer.classList.remove('is-loading');
      }
      setCartUpdating(false);
    }
  }

  async renderCartItems() {
    const cart = await this.fetchCart();
    if (!cart) return;
    // Paint immediately so the drawer feels instant, THEN sync the free pin-back in
    // the background and repaint only if it changed. The reconcile chain (extra
    // network round-trips — one free back per pin) is what made adds / qty-changes
    // feel slow, so it no longer blocks the first paint.
    this._paintCart(cart);
    // reconcileFreeBacks() serializes itself through the cart lock (shared with
    // the cart page's calls + every quantity write), reads the cart fresh, and
    // returns the reconciled cart to repaint from.
    const rc = await reconcileFreeBacks();
    if (rc.changed && rc.cart) this._paintCart(rc.cart);
    // Re-sync collection/home card "+" buttons' maxed state (is-maxed/aria-disabled)
    // against the cart we just settled on. Every drawer mutation (add, qty change,
    // remove) funnels through here, so this is the one place that needs to fire —
    // without it, removing a maxed item via the drawer's trash icon left its card
    // button permanently stuck disabled (syncButtons() previously only ran on page
    // load and after a successful quick-add, never after a removal).
    if (typeof CardQuickAdd !== 'undefined' && CardQuickAdd.syncButtons) CardQuickAdd.syncButtons();
  }

  // Paint the drawer from a cart object. Called optimistically, then again after the
  // free-back reconcile if it changed anything.
  _paintCart(cart) {
    if (!cart) return;
    // Update cart count badges
    this.cartCountEls.forEach(el => {
      el.textContent = cart.item_count;
      el.setAttribute('data-count', cart.item_count);
    });

    if (!this.itemsContainer) return;

    if (cart.item_count === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="btn btn--secondary btn--sm">Continue Shopping</a>
        </div>
      `;
    } else {
      let html = '';
      const renderedBuilds = new Set();
      // Group same variant+properties lines together for display. A discount
      // Function (e.g. the 4-pins-for-$20 deal) can SPLIT one purchase into
      // multiple real cart lines server-side — one discounted, one not — so a
      // shopper who bumps a pin from 1 to 5 sees it fracture into two rows.
      // The lines are still real/separate for order + refund records; this only
      // re-merges them for display, mirroring how the free pin-back and
      // build-your-own parts already get collapsed into one visual block below.
      const renderedGroups = new Set();
      cart.items.forEach(item => {
        if (isFreeBackLine(item)) { html += this.freeBackLineHTML(item); return; }
        // Build-your-own parts share a `_build` id — render the whole build as ONE grouped block.
        const buildId = item.properties && item.properties._build;
        if (buildId) {
          if (renderedBuilds.has(buildId)) return;
          renderedBuilds.add(buildId);
          const parts = cart.items.filter(i => i.properties && i.properties._build === buildId);
          html += this.buildGroupHTML(parts);
          return;
        }
        const groupKey = item.variant_id + '::' + JSON.stringify(item.properties || {});
        if (renderedGroups.has(groupKey)) return;
        renderedGroups.add(groupKey);
        const group = cart.items.filter(i =>
          !isFreeBackLine(i) &&
          !(i.properties && i.properties._build) &&
          (i.variant_id + '::' + JSON.stringify(i.properties || {})) === groupKey
        );
        html += this.cartLineHTML(group);
      });
      this.itemsContainer.innerHTML = html;

      // Atomic remove for a whole custom build (removes every part line at once)
      this.itemsContainer.querySelectorAll('[data-remove-build]').forEach(btn => {
        btn.addEventListener('click', () => {
          const keys = (btn.dataset.keys || '').split(',').filter(Boolean);
          this.removeBuild(keys);
        });
      });

      // Bind item-level events. A row's underlying `items` (possibly several real
      // lines split by a discount Function) is re-derived from `cart` by key here
      // rather than carried through the HTML string, so quantity edits always see
      // each line's current quantity.
      this.itemsContainer.querySelectorAll('.cart-item').forEach(item => {
        if (item.classList.contains('cart-item--freebie')) return; // locked line, no controls
        if (item.classList.contains('cart-item--build')) return;    // grouped build, atomic controls only
        const keys = (item.dataset.keys || '').split(',').filter(Boolean);
        if (!keys.length) return;
        const groupItems = cart.items.filter(i => keys.includes(i.key));
        const qtyInput = item.querySelector('[data-item-qty]');

        item.querySelector('[data-qty-minus]').addEventListener('click', () => {
          if (isCartBusy()) return; // a write is in flight — ignore the click (steppers are also visually disabled)
          const current = parseInt(qtyInput.value, 10);
          if (current > 1) {
            // Reflect the click immediately so a second rapid click reads the just-set
            // value instead of the pre-network-response DOM value — otherwise mashing
            // this button fires several updates that all compute the same target
            // (every click reads the same stale "current" until the full add/reconcile/
            // repaint round-trip finally lands).
            qtyInput.value = current - 1;
            this.updateGroupQuantity(groupItems, current - 1, qtyInput);
          } else {
            // At qty 1, the minus button removes the line entirely (matches the cart page).
            this.removeBuild(keys);
          }
        });

        item.querySelector('[data-qty-plus]').addEventListener('click', () => {
          if (isCartBusy()) return; // a write is in flight — ignore the click (steppers are also visually disabled)
          const current = parseInt(qtyInput.value, 10);
          qtyInput.value = current + 1;
          this.updateGroupQuantity(groupItems, current + 1, qtyInput);
        });

        qtyInput.addEventListener('change', debounce((e) => {
          if (isCartBusy()) return;
          const val = parseInt(e.target.value, 10);
          if (val > 0) {
            this.updateGroupQuantity(groupItems, val, qtyInput);
          }
        }, 600));

        item.querySelector('[data-remove]').addEventListener('click', () => {
          this.removeBuild(keys);
        });
      });
    }

    // Update subtotal / bundle savings / total (subtotal = pre-discount, so the
    // savings line has something to subtract from; Total is post-discount).
    if (cart) {
      const cartSavings = (cart.original_total_price || 0) - cart.total_price;
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.original_total_price);
      if (this.savingsEl) this.savingsEl.textContent = '-' + formatMoney(cartSavings);
      if (this.savingsRowEl) this.savingsRowEl.style.display = cartSavings > 0 ? '' : 'none';
      if (this.totalEl) this.totalEl.textContent = formatMoney(cart.total_price);
      this.updateDealReminder(cart);
    }

    // Update free-shipping bar (hide it while the cart is empty)
    if (cart) {
      updateFreeShipBars(cart.total_price);
      if (this.freeShipBar) this.freeShipBar.hidden = cart.item_count === 0;
    }
  }

  // Mirrors the Liquid cart-reminder mode in snippets/promo-banners.liquid, but
  // recomputed client-side since the drawer never re-fetches full HTML the way
  // the /cart page does. Uses isPinLine (the same pin-detection reconcileFreeBacks
  // relies on) rather than the rule's tag string — /cart.js items carry no `tags`
  // field, so tag-string matching isn't available client-side; product_type/_pin
  // is the only signal that survives to the AJAX cart JSON.
  updateDealReminder(cart) {
    if (!this.dealReminderEl) return;
    const rule = this._dealRule;
    if (!rule || !cart || !cart.items) { this.dealReminderEl.innerHTML = ''; return; }

    const backConfig = freeBackConfig();
    const backVariantId = backConfig ? backConfig.variantId : null;
    let count = 0;
    cart.items.forEach(i => { if (isPinLine(i, backVariantId)) count += i.quantity; });

    const rmin = rule.min;
    const remainder = count % rmin;
    const icon = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';

    if (count > 0 && remainder !== 0) {
      const remaining = rmin - remainder;
      const another = count < rmin ? '' : ' another';
      this.dealReminderEl.innerHTML = '<div class="cart-deal-reminder" role="status">' + icon +
        '<span>Add <strong>' + remaining + '</strong> more ' + this.escapeHtml(rule.tag) +
        ' to unlock' + another + ' <strong>' + this.escapeHtml(rule.message) + '</strong>.</span></div>';
    } else if (count > 0 && remainder === 0) {
      this.dealReminderEl.innerHTML = '<div class="cart-deal-reminder" role="status">' + icon +
        '<span><strong>' + this.escapeHtml(rule.message) + '</strong> unlocked \u{1F987}</span></div>';
    } else {
      this.dealReminderEl.innerHTML = '';
    }
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // Locked cart line for the auto-added free pin-back: no qty stepper, no remove
  // (theme-managed), priced as struck $1 -> Free once the discount applies.
  freeBackLineHTML(item) {
    const image = item.image
      ? `<img src="${item.image}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">`
      : `<div style="width:100%;height:100%;background:var(--color-bg-3);display:flex;align-items:center;justify-content:center;font-size:10px;color:var(--color-text-muted)">No image</div>`;
    const isFree = item.final_line_price === 0;
    const price = isFree
      ? `<span style="text-decoration:line-through;color:var(--color-text-muted);font-size:12px;">${formatMoney(item.original_line_price)}</span> <span style="color:var(--promo-banner-color, #c084fc);font-weight:600;letter-spacing:0.04em;">Free</span>`
      : formatMoney(item.final_line_price);
    return `
      <div class="cart-item cart-item--freebie" data-key="${item.key}">
        <div class="cart-item__image"><a href="${item.url}">${image}</a></div>
        <div class="cart-item__body">
          <div class="cart-item__row cart-item__row--top">
            <div class="cart-item__info">
              <a href="${item.url}" class="cart-item__title">${this.escapeHtml(item.product_title)}</a>
              <p style="font-size:11px;color:var(--promo-banner-color, #c084fc);margin:2px 0 0;">🎁 Free with your pins</p>
            </div>
          </div>
          <div class="cart-item__row cart-item__row--bottom">
            <span style="font-size:12px;color:var(--color-text-muted);">Qty ${item.quantity}</span>
            <p class="cart-item__price">${price}</p>
          </div>
        </div>
      </div>
    `;
  }

  // A normal product row — `items` is usually one real cart line, but a
  // quantity-break discount Function can split a single purchase into several
  // (one discounted, one not), so this always renders whatever's in the group
  // as ONE combined row: summed quantity, summed price, and the savings (if
  // any) shown underneath. Quantity/remove controls act on the whole group.
  cartLineHTML(items) {
    const first = items[0];
    const totalQty = items.reduce((n, i) => n + i.quantity, 0);
    const totalPrice = items.reduce((n, i) => n + i.final_line_price, 0);
    const totalOriginal = items.reduce((n, i) => n + i.original_line_price, 0);
    const savings = totalOriginal - totalPrice;
    const keys = items.map(i => i.key).join(',');
    let dealTitle = '';
    for (const i of items) {
      if (i.line_level_discount_allocations && i.line_level_discount_allocations.length) {
        dealTitle = i.line_level_discount_allocations[0].discount_application.title;
        break;
      }
    }
    const image = first.image
      ? `<img src="${first.image}" alt="${this.escapeHtml(first.product_title)}" loading="lazy">`
      : `<div style="width:100%;height:100%;background:var(--color-bg-3);display:flex;align-items:center;justify-content:center;font-size:10px;color:var(--color-text-muted)">No image</div>`;
    const priceHTML = savings > 0
      ? `<span style="text-decoration:line-through;color:var(--color-text-muted);font-size:12px;">${formatMoney(totalOriginal)}</span> ${formatMoney(totalPrice)}`
      : formatMoney(totalPrice);

    return `
      <div class="cart-item" data-keys="${keys}">
        <div class="cart-item__image">
          <a href="${first.url}">${image}</a>
        </div>
        <div class="cart-item__body">
          <div class="cart-item__row cart-item__row--top">
            <div class="cart-item__info">
              <a href="${first.url}" class="cart-item__title">${this.escapeHtml(first.product_title)}</a>
              ${first.variant_title ? `<p class="cart-item__variant">${this.escapeHtml(first.variant_title)}</p>` : ''}
              ${savings > 0 ? `<p style="font-size:11px;color:var(--promo-banner-color, #c084fc);margin:2px 0 0;">${this.escapeHtml(dealTitle)} · Save ${formatMoney(savings)}</p>` : ''}
            </div>
            <button type="button" class="cart-item__remove" data-remove aria-label="Remove item" title="Remove">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6"/>
                <path d="M14 11v6"/>
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </button>
          </div>
          <div class="cart-item__row cart-item__row--bottom">
            <div class="cart-item__qty">
              <button type="button" class="qty-minus" data-qty-minus aria-label="Decrease quantity">−</button>
              <input type="number" value="${totalQty}" min="1" class="cart-item-qty" data-item-qty>
              <button type="button" class="qty-plus" data-qty-plus aria-label="Increase quantity">+</button>
            </div>
            <p class="cart-item__price">${priceHTML}</p>
          </div>
        </div>
      </div>
    `;
  }

  // One grouped cart block for a build-your-own piece: header + each part + a
  // combined subtotal + a single Remove that drops every part line together.
  buildGroupHTML(parts) {
    const label = (parts[0].properties && parts[0].properties._build_label) || 'Custom build';
    const keys = parts.map(p => p.key).join(',');
    let total = 0;
    parts.forEach(p => { total += p.final_line_price; });
    // Base part first so its thumbnail leads the group.
    const ordered = parts.slice().sort((a, b) => {
      const ra = (a.properties && a.properties._build_role) === 'base' ? 0 : 1;
      const rb = (b.properties && b.properties._build_role) === 'base' ? 0 : 1;
      return ra - rb;
    });
    const rows = ordered.map(p => {
      const img = p.image
        ? `<img src="${p.image}" alt="${this.escapeHtml(p.product_title)}" loading="lazy">`
        : '';
      return `
        <div class="build-part">
          <div class="build-part__img">${img}</div>
          <span class="build-part__name">${this.escapeHtml(p.product_title)}</span>
          <span class="build-part__price">${formatMoney(p.final_line_price)}</span>
        </div>`;
    }).join('');
    return `
      <div class="cart-item cart-item--build">
        <div class="cart-item--build__head">
          <span class="cart-item--build__label">🖤 ${this.escapeHtml(label)}</span>
          <button type="button" class="cart-item__remove" data-remove-build data-keys="${keys}" aria-label="Remove ${this.escapeHtml(label)}" title="Remove build">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </button>
        </div>
        <div class="cart-item--build__parts">${rows}</div>
        <div class="cart-item--build__foot">
          <span>Subtotal</span>
          <span class="cart-item__price">${formatMoney(total)}</span>
        </div>
      </div>`;
  }

  // Remove every line of a custom build in one request (line item keys -> 0).
  async removeBuild(keys) {
    if (!keys || !keys.length) return;
    if (this.itemsContainer) this.itemsContainer.classList.add('is-loading');
    try {
      const updates = {};
      keys.forEach(k => { updates[k] = 0; });
      const ok = await withCartLock(() => postCart('/cart/update.js', { updates }));
      if (!ok) throw new Error('Cart update failed');
      await this.renderCartItems();
    } catch (err) {
      console.error('[CartDrawer] removeBuild error:', err);
    } finally {
      if (this.itemsContainer) this.itemsContainer.classList.remove('is-loading');
    }
  }

  bindEvents() {
    // Cart toggle buttons
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.drawer.classList.contains('is-open')) {
          this.closeDrawer();
        } else {
          this.openDrawer();
        }
      });
    });

    // Close button
    document.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeDrawer());
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeDrawer();
        MobileMenu.close();
        SearchBar.close();
      });
    }

    // Add-to-cart form intercept
    document.querySelectorAll('[data-product-form]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[data-add-to-cart]');
        const btnText = form.querySelector('[data-add-to-cart-text]');
        const originalText = btnText ? btnText.textContent : 'Add to Cart';

        if (btn) btn.disabled = true;
        if (btnText) btnText.textContent = 'Adding...';

        const formData = new FormData(form);
        await this.addToCart(formData);

        if (btn) btn.disabled = false;
        if (btnText) btnText.textContent = originalText;
      });
    });
  }
}

/* ============================================================
   2. MEGA MENU
   ============================================================ */

const MegaMenu = {
  init() {
    const navItems = document.querySelectorAll('.nav-item.has-megamenu');

    const setExpanded = (item, open) => {
      item.querySelector('.nav-link')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    navItems.forEach(item => {
      let leaveTimer;

      item.addEventListener('mouseenter', () => {
        clearTimeout(leaveTimer);
        navItems.forEach(other => {
          if (other !== item) {
            other.querySelector('.mega-menu')?.classList.remove('is-visible');
            setExpanded(other, false);
          }
        });
        item.querySelector('.mega-menu')?.classList.add('is-visible');
        setExpanded(item, true);
        this.clampToViewport(item.querySelector('.mega-menu'));
      });

      item.addEventListener('mouseleave', () => {
        leaveTimer = setTimeout(() => {
          item.querySelector('.mega-menu')?.classList.remove('is-visible');
          setExpanded(item, false);
        }, 150);
      });

      // Keyboard: opening is handled by CSS :focus-within; keep aria-expanded in
      // sync as focus enters/leaves the item (and close others).
      item.addEventListener('focusin', () => {
        navItems.forEach(other => { if (other !== item) setExpanded(other, false); });
        setExpanded(item, true);
        this.clampToViewport(item.querySelector('.mega-menu'));
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) setExpanded(item, false);
      });

      const panel = item.querySelector('.mega-menu');
      if (panel) {
        panel.addEventListener('mouseenter', () => clearTimeout(leaveTimer));
        panel.addEventListener('mouseleave', () => {
          leaveTimer = setTimeout(() => {
            panel.classList.remove('is-visible');
            this.resetDesktopDrill(panel);
          }, 150);
        });
      }

      // Drill-down dropdowns (opt-in via the "Drill-down dropdowns" setting):
      // clicking a group swaps the root panel for that group's links. Mirrors
      // the mobile drawer's data-drill-* contract but is scoped to this panel,
      // so the two never fight over the same elements.
      const drill = item.querySelector('[data-desktop-drill]');
      if (drill) {
        drill.addEventListener('click', (e) => {
          const opener = e.target.closest('[data-drill-open]');
          if (opener) {
            e.preventDefault();
            this.swapDesktopPanel(drill, opener.dataset.drillOpen);
            return;
          }
          const back = e.target.closest('[data-drill-back]');
          if (back) {
            e.preventDefault();
            this.swapDesktopPanel(drill, back.dataset.drillBack);
          }
        });
        // Leaving the nav item entirely rewinds to the group list, so the next
        // hover never opens mid-drill from a previous visit.
        item.addEventListener('mouseleave', () => {
          setTimeout(() => this.resetDesktopDrill(drill), 200);
        });
      }
    });

    // Keyboard: close mega menu on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.mega-menu.is-visible').forEach(m => {
          m.classList.remove('is-visible');
        });
        document.querySelectorAll('.nav-item.has-megamenu .nav-link[aria-expanded="true"]').forEach(l => {
          l.setAttribute('aria-expanded', 'false');
        });
      }
    });
  },

  // Every panel starts centered under its trigger (--menu-shift: 0px, set in
  // CSS). Measure that natural position and nudge it back on-screen if it
  // would overflow either viewport edge — replaces the old last-child-only
  // right:0 special case, and works for ANY item near an edge (not just the
  // last one), since width varies by content (long category names, custom
  // mega_menu_group columns) and can't be known in advance from CSS alone.
  clampToViewport(panel) {
    if (!panel) return;
    panel.style.setProperty('--menu-shift', '0px');
    const rect = panel.getBoundingClientRect();
    const margin = 12;
    let shift = 0;
    if (rect.left < margin) shift = margin - rect.left;
    else if (rect.right > window.innerWidth - margin) shift = (window.innerWidth - margin) - rect.right;
    panel.style.setProperty('--menu-shift', shift + 'px');
  },

  // Show one drill panel by name ("root" or a group index), hide the rest.
  // Re-clamps afterwards because panel widths differ between the group list and
  // a group's link list, which can change whether it overflows the viewport.
  swapDesktopPanel(drill, name) {
    if (!drill || !name) return;
    const target = drill.querySelector(`[data-drill-panel="${name}"]`);
    if (!target) return;
    drill.querySelectorAll('[data-drill-panel]').forEach(p => { p.hidden = p !== target; });
    this.clampToViewport(drill.closest('.mega-menu') || drill);
  },

  // Rewind a drill-down dropdown to its group list.
  resetDesktopDrill(scope) {
    if (!scope) return;
    const drill = scope.matches?.('[data-desktop-drill]')
      ? scope
      : scope.querySelector?.('[data-desktop-drill]');
    if (!drill) return;
    drill.querySelectorAll('[data-drill-panel]').forEach(p => {
      p.hidden = p.dataset.drillPanel !== 'root';
    });
  }
};

/* ============================================================
   3. MOBILE MENU
   ============================================================ */

const MobileMenu = {
  menu: null,
  overlay: null,

  init() {
    this.menu = document.querySelector('[data-mobile-menu]');
    this.overlay = document.querySelector('[data-overlay]');
    this.hamburger = document.querySelector('.hamburger');

    document.querySelectorAll('[data-mobile-menu-toggle]').forEach(btn => {
      btn.addEventListener('click', () => this.toggle());
    });

    // Close on Escape while open, returning focus to the hamburger.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu?.classList.contains('is-open')) this.close();
    });

    // The whole nav is one drill stack (root -> per-category panel -> leaf
    // links), unified top to bottom — no separate inline accordion step.
    // Panels swap via [hidden] + a short opacity crossfade; container height
    // just follows whichever panel is visible, no JS height tracking needed.
    this.menu?.addEventListener('click', (e) => {
      // Drill in: crossfade to the target panel.
      const drillOpen = e.target.closest('[data-drill-open]');
      if (drillOpen) {
        const drill = drillOpen.closest('[data-mobile-drill]');
        if (!drill) return;
        const targetPanel = drill.querySelector(`[data-drill-panel="${drillOpen.dataset.drillOpen}"]`);
        if (targetPanel) this.swapDrillPanel(drill, targetPanel);
        return;
      }

      // Drill back: crossfade to whichever panel this back row names.
      const drillBack = e.target.closest('[data-drill-back]');
      if (drillBack) {
        const drill = drillBack.closest('[data-mobile-drill]');
        const targetPanel = drill?.querySelector(`[data-drill-panel="${drillBack.dataset.drillBack}"]`);
        if (targetPanel) this.swapDrillPanel(drill, targetPanel);
      }
    });
  },

  // Swap the visible drill panel with a short opacity crossfade instead of an
  // instant [hidden] toggle. The old panel fades out first, THEN gets hidden
  // and the new one revealed+faded in.
  swapDrillPanel(drill, targetPanel) {
    const current = drill.querySelector(':scope > [data-drill-panel]:not([hidden])');
    if (!current || current === targetPanel) return;
    current.classList.add('is-fading');
    setTimeout(() => {
      current.hidden = true;
      current.classList.remove('is-fading');
      targetPanel.hidden = false;
      targetPanel.classList.add('is-fading');
      void targetPanel.offsetHeight; // force reflow so opacity:0 commits before removing the class
      targetPanel.classList.remove('is-fading');
    }, 100);
  },

  toggle() {
    if (this.menu?.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  },

  open() {
    this.menu?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('no-scroll');
    this.hamburger?.classList.add('is-open');
    this.menu?.setAttribute('aria-hidden', 'false');
    this.hamburger?.setAttribute('aria-expanded', 'true');
    // Move focus into the open drawer (the close button) for keyboard/AT users.
    this.menu?.querySelector('.mobile-menu__close')?.focus();
  },

  close() {
    this.menu?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    this.hamburger?.classList.remove('is-open');
    this.menu?.setAttribute('aria-hidden', 'true');
    this.hamburger?.setAttribute('aria-expanded', 'false');
    this.hamburger?.focus();
    this.resetMobileDrill();
  },

  // Rewind the drill stack back to the root panel, so reopening the drawer
  // never shows stale drilled-in state from a previous visit.
  resetMobileDrill() {
    this.menu?.querySelectorAll('[data-mobile-drill]').forEach(drill => {
      const rootPanel = drill.querySelector('[data-drill-panel="root"]');
      drill.querySelectorAll('[data-drill-panel]').forEach(p => {
        p.hidden = p !== rootPanel;
        p.classList.remove('is-fading');
      });
    });
  }
};

/* ============================================================
   4. ANNOUNCEMENT BAR
   ============================================================ */

const AnnouncementBar = {
  storageKey: 'nocturne_announcement_closed',

  init() {
    const bar = document.querySelector('[data-announcement-bar]');
    if (!bar) return;

    // Check if previously closed in this session
    if (sessionStorage.getItem(this.storageKey) === 'true') {
      bar.classList.add('is-hidden');
      return;
    }

    const closeBtn = bar.querySelector('[data-announcement-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        bar.classList.add('is-hidden');
        sessionStorage.setItem(this.storageKey, 'true');
      });
    }
  }
};

/* ============================================================
   5. SEARCH TOGGLE
   ============================================================ */

const SearchBar = {
  bar: null,

  init() {
    this.bar = document.querySelector('[data-search-bar]');
    if (!this.bar) return;

    document.querySelectorAll('[data-search-toggle]').forEach(btn => {
      btn.addEventListener('click', () => this.toggle());
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.bar.classList.contains('is-open')) {
        this.close();
      }
    });
  },

  toggle() {
    if (this.bar.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  },

  open() {
    this.bar.classList.add('is-open');
    const input = this.bar.querySelector('.search-input');
    if (input) {
      setTimeout(() => input.focus(), 200);
    }
  },

  close() {
    this.bar.classList.remove('is-open');
  }
};

/* ============================================================
   6. COLLECTION FILTERS
   ============================================================ */

const CollectionFilters = {
  init() {
    this.layout = document.querySelector('.collection-layout');
    if (!this.layout) return;

    // Flag the page so the Apply Filters button is hidden via CSS. The button
    // stays in the markup as a no-JS fallback. Using a class (not inline style)
    // means it stays hidden after the sidebar is swapped by AJAX.
    document.body.classList.add('js-collection-filters');

    // Open the first two filter groups by default on initial load.
    this.openDefaultGroups();

    // Event delegation — survives DOM swaps from AJAX, so nothing is re-bound.
    document.addEventListener('click', (e) => this.onClick(e));
    document.addEventListener('change', (e) => this.onChange(e));
    document.addEventListener('submit', (e) => this.onSubmit(e));
    window.addEventListener('popstate', () => this.fetchAndRender(window.location.href, false));
  },

  openDefaultGroups() {
    const groups = document.querySelectorAll('.collection-filters [data-filter-group]');
    groups.forEach((group, i) => {
      if (i < 2) group.classList.add('is-open');
    });
  },

  openMobileFilters() {
    const filters = document.querySelector('.collection-filters');
    if (!filters) return;
    filters.classList.add('is-mobile-open');
    document.body.classList.add('no-scroll');
  },

  closeMobileFilters() {
    const filters = document.querySelector('.collection-filters');
    if (filters) filters.classList.remove('is-mobile-open');
    document.body.classList.remove('no-scroll');
  },

  onClick(e) {
    // Accordion toggle
    const toggle = e.target.closest('[data-filter-toggle]');
    if (toggle) {
      const group = toggle.closest('[data-filter-group]');
      if (group) group.classList.toggle('is-open');
      return;
    }

    // Mobile filter panel open
    if (e.target.closest('[data-mobile-filter-toggle]')) {
      this.openMobileFilters();
      return;
    }

    // Mobile filter panel close
    if (e.target.closest('[data-mobile-filter-close]')) {
      this.closeMobileFilters();
      return;
    }

    // Active filter chips, "Clear all", and pagination — AJAX instead of reload
    const link = e.target.closest('.active-filters a, .pagination a');
    if (link && link.href) {
      e.preventDefault();
      this.fetchAndRender(link.href);
    }
  },

  onChange(e) {
    // Checkbox filter selection
    if (e.target.matches('#FilterForm input[type="checkbox"]')) {
      this.fetchAndRender(this.urlFromForm());
      return;
    }
    // Sort dropdown
    if (e.target.id === 'CollectionSortBy') {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      this.fetchAndRender(url.toString());
    }
  },

  onSubmit(e) {
    // Price range "Go" button (or any submit of the filter form)
    if (e.target.id === 'FilterForm') {
      e.preventDefault();
      this.fetchAndRender(this.urlFromForm());
    }
  },

  urlFromForm() {
    const form = document.getElementById('FilterForm');
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    const query = params.toString();
    return form.action + (query ? '?' + query : '');
  },

  // Record which accordion groups are open so we can restore them after a swap.
  getOpenState() {
    return Array.from(document.querySelectorAll('.collection-filters [data-filter-group]'))
      .map(g => g.classList.contains('is-open'));
  },

  restoreOpenState(state) {
    document.querySelectorAll('.collection-filters [data-filter-group]').forEach((g, i) => {
      g.classList.toggle('is-open', !!state[i]);
    });
  },

  // Animate an element's height from a previous value to its new natural
  // height, then release back to auto so the layout stays responsive.
  animateHeight(el, fromHeight) {
    const toHeight = el.offsetHeight;
    if (!fromHeight || !toHeight || fromHeight === toHeight) return;

    el.style.overflow = 'hidden';
    el.style.height = fromHeight + 'px';
    void el.offsetHeight; // force reflow so the start height is applied
    el.style.transition = 'height 0.3s ease';
    el.style.height = toHeight + 'px';

    let timer;
    const cleanup = () => {
      el.style.height = '';
      el.style.transition = '';
      el.style.overflow = '';
      el.removeEventListener('transitionend', cleanup);
      clearTimeout(timer);
    };
    el.addEventListener('transitionend', cleanup);
    timer = setTimeout(cleanup, 400); // fallback if transitionend doesn't fire
  },

  async fetchAndRender(url, push = true) {
    const main = document.querySelector('.collection-main');
    if (main) main.classList.add('is-loading');

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Filter fetch failed');
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const newToolbar = doc.querySelector('.collection-toolbar');
      const newFilters = doc.querySelector('.collection-filters');
      const newMain = doc.querySelector('.collection-main');
      if (!newMain) throw new Error('Could not parse filtered results');

      // Preserve which filter groups are expanded across the swap.
      const openState = this.getOpenState();

      const curToolbar = document.querySelector('.collection-toolbar');
      const curFilters = document.querySelector('.collection-filters');
      const curMain = document.querySelector('.collection-main');

      // Keep the mobile filter overlay open across the swap (so selecting a
      // filter doesn't close the panel).
      const wasMobileOpen = curFilters && curFilters.classList.contains('is-mobile-open');

      // Capture the current results height before swapping so we can animate
      // from it to the new height.
      const oldMainHeight = curMain.offsetHeight;

      if (newToolbar && curToolbar) curToolbar.replaceWith(newToolbar);
      if (newFilters && curFilters) curFilters.replaceWith(newFilters);
      curMain.replaceWith(newMain);

      // Re-arm the loading shimmer over the freshly swapped-in cards.
      CardMediaLoad.init(newMain);

      if (wasMobileOpen && newFilters) newFilters.classList.add('is-mobile-open');

      this.restoreOpenState(openState);

      // Smoothly grow/shrink the results area instead of an abrupt page jump.
      this.animateHeight(newMain, oldMainHeight);

      if (push) history.pushState({}, '', url);
    } catch (err) {
      console.error('[CollectionFilters] fetchAndRender error:', err);
      window.location = url; // graceful fallback to full navigation
    } finally {
      const m = document.querySelector('.collection-main');
      if (m) m.classList.remove('is-loading');
    }
  }
};

/* ============================================================
   7. QUANTITY SELECTOR (Product Page)
   ============================================================ */

const QuantitySelector = {
  init() {
    this.inventory = window.productInventory || {};

    document.querySelectorAll('.product-form').forEach(form => {
      const minusBtn = form.querySelector('[data-qty-minus]');
      const plusBtn = form.querySelector('[data-qty-plus]');
      const input = form.querySelector('[data-qty-input]');

      if (!minusBtn || !plusBtn || !input) return;

      // Low-stock hint element, inserted under the quantity/add-to-cart row.
      let hint = form.querySelector('[data-qty-hint]');
      if (!hint) {
        hint = document.createElement('p');
        hint.className = 'qty-hint';
        hint.setAttribute('data-qty-hint', '');
        hint.style.display = 'none';
        const row = input.closest('.quantity-selector')
          ? input.closest('.quantity-selector').parentElement
          : input.parentElement;
        if (row && row.parentElement) {
          row.parentElement.insertBefore(hint, row.nextSibling);
        }
      }

      // Max quantity addable for the currently selected variant.
      // Infinity = untracked or overselling allowed (no cap).
      const getMax = () => {
        const idInput = form.querySelector('[data-variant-id]');
        const id = idInput ? idInput.value : null;
        const max = id != null && Object.prototype.hasOwnProperty.call(this.inventory, id)
          ? this.inventory[id]
          : null;
        return (typeof max === 'number') ? max : Infinity;
      };

      const clamp = () => {
        const max = getMax();
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) val = 1;
        if (val > max) val = max < 1 ? 1 : max;
        input.value = val;

        if (max === Infinity) {
          input.removeAttribute('max');
        } else {
          input.max = max;
        }

        // Surface a hint only when actually out of stock (no "Only N left" scarcity messaging).
        if (max === 0) {
          hint.textContent = 'Out of stock';
          hint.style.display = '';
        } else {
          hint.textContent = '';
          hint.style.display = 'none';
        }
      };

      minusBtn.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 1;
        if (current > 1) input.value = current - 1;
        clamp();
      });

      plusBtn.addEventListener('click', () => {
        const max = getMax();
        const current = parseInt(input.value, 10) || 1;
        if (current < max) {
          input.value = current + 1;
        } else if (max !== Infinity) {
          showToast(max > 0 ? `Only ${max} available in stock.` : 'Out of stock.');
        }
        clamp();
      });

      input.addEventListener('change', clamp);

      // Re-clamp whenever the shopper switches variant (stock differs per variant).
      document.addEventListener('variant:changed', clamp);

      clamp();
    });
  }
};

/* ============================================================
   8. PRODUCT IMAGE GALLERY
   ============================================================ */

const ProductGallery = {
  init() {
    this.main = document.querySelector('#ProductMainImage');
    if (!this.main) return;

    this.thumbs = Array.from(document.querySelectorAll('[data-thumbnail]'));
    this.main.style.transition = 'opacity 0.2s ease';

    // Preload every full-size gallery image up front so switching between them
    // is instant instead of fetching from the network on each click.
    this.thumbs.forEach(thumb => {
      if (thumb.dataset.imageUrl) {
        const preload = new Image();
        preload.src = thumb.dataset.imageUrl;
      }
    });

    // Thumbnail clicks
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.setActive(thumb));
    });

    // Prev / next arrows (only present when there are multiple images)
    const prev = document.querySelector('[data-gallery-prev]');
    const next = document.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', () => this.step(-1));
    if (next) next.addEventListener('click', () => this.step(1));

    // Zoom: magnifier button or clicking the image opens the in-page viewer
    const zoomBtn = document.querySelector('[data-zoom-trigger]');
    if (zoomBtn) zoomBtn.addEventListener('click', () => this.openLightbox());
    this.main.addEventListener('click', () => this.openLightbox());

    this.buildLightbox();
  },

  setActive(thumb) {
    if (thumb.dataset.imageUrl) this.swapMain(thumb.dataset.imageUrl);
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  },

  swapMain(url) {
    this.main.style.opacity = '0';
    this.main.onload = () => { this.main.style.opacity = '1'; };
    this.main.removeAttribute('srcset');
    this.main.src = url;
  },

  step(dir) {
    if (this.thumbs.length === 0) return;
    const current = this.thumbs.findIndex(t => t.classList.contains('is-active'));
    const start = current < 0 ? 0 : current;
    const i = (start + dir + this.thumbs.length) % this.thumbs.length;
    this.setActive(this.thumbs[i]);
  },

  // Higher-resolution version of whatever image is currently shown
  largeUrl() {
    const src = this.main.currentSrc || this.main.src;
    return src.replace(/([?&]width=)\d+/, '$11600');
  },

  buildLightbox() {
    const lb = document.createElement('div');
    lb.className = 'product-lightbox';
    lb.setAttribute('aria-hidden', 'true');
    lb.innerHTML =
      '<button type="button" class="product-lightbox__close" aria-label="Close">×</button><img alt="">';
    document.body.appendChild(lb);

    this.lightbox = lb;
    this.lightboxImg = lb.querySelector('img');

    lb.addEventListener('click', () => this.closeLightbox());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeLightbox();
    });
  },

  openLightbox() {
    if (!this.lightbox) return;
    this.lightboxImg.src = this.largeUrl();
    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
  },

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  }
};

/* ============================================================
   9. VARIANT PICKER
   ============================================================ */

const VariantPicker = {
  product: null,

  init() {
    if (!window.productJSON) return;

    this.product = window.productJSON;

    // Listen for variant changes
    const form = document.querySelector('[data-product-form]');
    if (!form) return;

    form.querySelectorAll('.variant-radio').forEach(radio => {
      radio.addEventListener('change', () => this.updateVariant(form));
    });

    // Init with first variant
    this.updateVariant(form);
  },

  getSelectedOptions(form) {
    const options = [];
    const groups = form.querySelectorAll('.variant-option');
    groups.forEach((group, index) => {
      const checked = group.querySelector(`.variant-radio:checked`);
      if (checked) {
        options.push(checked.value);
      }
    });
    return options;
  },

  updateOptionAvailability(form, selectedOptions) {
    if (!this.product || !this.product.variants) return;
    const groups = form.querySelectorAll('.variant-option');
    groups.forEach((group, groupIndex) => {
      group.querySelectorAll('.variant-radio').forEach(radio => {
        const testOptions = selectedOptions.slice();
        testOptions[groupIndex] = radio.value;
        const available = this.product.variants.some(v =>
          testOptions.every((opt, i) => v[`option${i + 1}`] === opt) && v.available
        );
        const label = form.querySelector(`label[for="${radio.id}"]`);
        if (label) label.classList.toggle('is-unavailable', !available);
      });
    });
  },

  findVariant(selectedOptions) {
    if (!this.product || !this.product.variants) return null;

    return this.product.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        return variant[`option${index + 1}`] === option;
      });
    }) || null;
  },

  updateVariant(form) {
    const selectedOptions = this.getSelectedOptions(form);
    const variant = this.findVariant(selectedOptions);

    if (!variant) return;

    // Update hidden ID field
    const idInput = form.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    // Let the quantity selector re-apply the stock cap for this variant.
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variantId: variant.id } }));

    // Update price display
    const priceContainer = document.querySelector('[data-price]');
    if (priceContainer) {
      let priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const savings = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        priceHtml = `
          <span class="price price--sale">${formatMoney(variant.price)}</span>
          <span class="price price--compare">${formatMoney(variant.compare_at_price)}</span>
          <span class="price-badge">Save ${savings}%</span>
        `;
      } else {
        priceHtml = `<span class="price">${formatMoney(variant.price)}</span>`;
      }
      priceContainer.innerHTML = priceHtml;
    }

    // Update add-to-cart button
    const addBtn = form.querySelector('[data-add-to-cart]');
    const addBtnText = form.querySelector('[data-add-to-cart-text]');
    if (addBtn && addBtnText) {
      if (variant.available) {
        addBtn.disabled = false;
        addBtnText.textContent = 'Add to Cart';
      } else {
        addBtn.disabled = true;
        addBtnText.textContent = 'Sold Out';
      }
    }

    // Mark unavailable option values
    this.updateOptionAvailability(form, selectedOptions);

    // Update featured image if variant has one
    if (variant.featured_image) {
      const mainImage = document.querySelector('#ProductMainImage');
      if (mainImage) {
        // Build image URL with width param
        const imgSrc = variant.featured_image.src.split('?')[0];
        mainImage.src = imgSrc;

        // Sync thumbnail active state
        document.querySelectorAll('[data-thumbnail]').forEach(thumb => {
          thumb.classList.remove('is-active');
          if (thumb.dataset.imageUrl && thumb.dataset.imageUrl.includes(imgSrc.split('/').pop().split('.')[0])) {
            thumb.classList.add('is-active');
          }
        });
      }
    }
  }
};

/* ============================================================
   10. (removed) HEADER SCROLL BEHAVIOR
   Header is now STATIC: full 72px height at all times, no
   condense-on-scroll and no scroll-driven background shift. Its
   frosted background is the constant .site-header::before layer.
   (User 2026-07-15 preferred a static bar over shrink-on-scroll.)
   ============================================================ */

/* ============================================================
   11. CART PAGE (static cart updates)
   ============================================================ */

const CartPage = {
  init() {
    this.bindCartForms();
    this.initFreeShip();
    this.initRecommendations();
    this.syncFreeBacksOnLoad();
  },

  // On landing on /cart, reconcile the free pin-back once (e.g. a returning cart
  // with pins but no freebie, or a stale freebie), then re-render if it changed.
  async syncFreeBacksOnLoad() {
    if (!document.querySelector('[data-cart-update-form]')) return;
    const rc = await reconcileFreeBacks();
    if (rc.changed) await this.refreshCartContents();
  },

  // Cart line quantity + remove handlers. Re-callable after an in-place refresh.
  // A visual row's underlying `data-line-keys` / `data-remove-keys` can list
  // MORE THAN ONE real cart line — a quantity-break discount Function (e.g. the
  // 4-pins-for-$20 deal) can split one purchase into several lines server-side
  // (one discounted, one not), and this page combines them into one row for
  // display (see the grouping loop in main-cart.liquid). Quantity/remove
  // controls act on the whole group at once.
  bindCartForms() {
    document.querySelectorAll('[data-cart-update-form]').forEach(form => {
      const commitQty = async (el) => {
        const keys = (el.dataset.lineKeys || '').split(',').filter(Boolean);
        let qty = parseInt(el.value, 10);
        if (isNaN(qty)) return;
        // Cap to available stock when a max (inventory limit) is set
        const max = parseInt(el.dataset.max, 10);
        if (!isNaN(max) && qty > max) {
          qty = max;
          el.value = max;
          showToast(max > 0 ? `Only ${max} available in stock.` : 'Out of stock.');
        }
        if (!keys.length || qty < 0) return;
        const row = el.closest('[data-cart-row]');
        if (row) row.classList.add('is-updating');
        await this.updateGroupLine(keys, qty);
      };
      const debouncedCommit = debounce((e) => commitQty(e.target), 700);

      form.querySelectorAll('[data-item-qty-input]').forEach(input => {
        input.addEventListener('change', (e) => {
          const el = e.target;
          // Dropping to 0 is a removal, not a quantity edit — commit it right away
          // instead of waiting out the typing-debounce, which is what made the row
          // visibly hang at 0 for up to 700ms before anything happened.
          if ((parseInt(el.value, 10) || 0) === 0) {
            const row = el.closest('[data-cart-row]');
            if (row) row.classList.add('is-updating');
            commitQty(el);
          } else {
            debouncedCommit(e);
          }
        });
      });

      form.querySelectorAll('[data-remove-line]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const keys = (btn.dataset.removeKeys || '').split(',').filter(Boolean);
          if (keys.length) {
            const row = btn.closest('[data-cart-row]');
            if (row) row.classList.add('is-updating');
            await this.updateGroupLine(keys, 0);
            if (row) row.remove();
          }
        });
      });
    });
  },

  // Refresh the cart items + summary + free-ship bar in place by re-fetching the
  // cart page, WITHOUT touching the recommendations grid (so quick-added cards stay
  // available for adding another size). Returns true on success.
  async refreshCartContents() {
    try {
      const res = await fetch(window.location.pathname + window.location.search, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) return false;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      // If the cart just became empty, a full reload renders the empty state cleanly.
      if (!doc.querySelector('.cart-layout')) { window.location.reload(); return true; }
      ['.cart-free-ship', '.cart-deal-reminder-wrap', '.cart-layout'].forEach(sel => {
        const fresh = doc.querySelector(sel);
        const current = document.querySelector(sel);
        if (fresh && current) current.replaceWith(fresh);
      });
      this.bindCartForms();
      this.initFreeShip();
      return true;
    } catch (e) {
      return false;
    }
  },

  // Set a visual row's TOTAL quantity across however many real cart lines it
  // currently spans (usually 1, but a discount Function can split one purchase
  // into several — see the grouping loop in main-cart.liquid). Keeps the
  // largest existing line as the survivor and zeroes the rest — same
  // "collapse to one line" pattern reconcileFreeBacks() uses for the free pin
  // back, applied generically here to any grouped row.
  async updateGroupLine(keys, quantity) {
    setCartUpdating(true); // veil on + steppers disabled for the whole write→reconcile→refresh chain
    try {
      const targetQty = Math.max(0, parseInt(quantity, 10) || 0);
      const current = await fetchCartJson();
      const items = (current && current.items || []).filter(i => keys.includes(i.key));
      if (!items.length) { await this.refreshCartContents(); return; }
      const sorted = items.slice().sort((a, b) => b.quantity - a.quantity);
      const survivor = sorted[0];
      const updates = { [survivor.key]: targetQty };
      sorted.slice(1).forEach(i => { updates[i.key] = 0; });

      // Optimistic free-ship bump — see the drawer's updateGroupQuantity for why:
      // the write + reconcile + full-page refresh chain below is several sequential
      // round-trips, so nudge the bar now with the price we already have in hand.
      // refreshCartContents() replaces this node with the authoritative one anyway.
      const haveTotal = sorted.reduce((n, l) => n + l.quantity, 0);
      const unitPrice = typeof survivor.price === 'number' ? survivor.price : 0;
      if (unitPrice) {
        const bar = document.querySelector('[data-cart-free-ship]');
        if (bar) {
          const cur = parseInt(bar.dataset.current, 10) || 0;
          updateFreeShipBars(cur + (targetQty - haveTotal) * unitPrice);
        }
      }

      // Serialized so this quantity write can never overlap the free-back
      // reconcile (or another write) — that overlap was corrupting the back count.
      const ok = await withCartLock(() => postCart('/cart/update.js', { updates }));
      if (!ok) throw new Error('Cart update failed');

      // Sync the free pin-back to the new pin count. reconcileFreeBacks() runs as
      // its own locked step, reading the cart fresh — so it always reconciles to
      // the post-write pin count, never a stale snapshot.
      await reconcileFreeBacks();

      // ALWAYS re-render the whole summary from the server (subtotal + cart-level
      // discounts + the amount-due Total, and the fresh post-split line grouping),
      // so a quantity change can never leave any of that stale. refreshCartContents
      // reloads cleanly if the cart is now empty.
      await this.refreshCartContents();
    } catch (err) {
      console.error('[CartPage] updateGroupLine error:', err);
    } finally {
      setCartUpdating(false);
    }
  },

  /* ---- Free-shipping progress bar (delegates to the shared multi-bar updater) ---- */
  initFreeShip() {
    const bar = document.querySelector('[data-cart-free-ship]');
    if (!bar) return;
    updateFreeShipBars(parseInt(bar.dataset.current, 10) || 0);
  },

  updateFreeShip(totalCents) {
    updateFreeShipBars(totalCents);
  },

  /* ---- Cart upsell / recommendations ---- */
  async initRecommendations() {
    const wrap = document.querySelector('[data-cart-recommendations]');
    if (!wrap) return;
    const productId = wrap.dataset.recProductId;
    if (!productId) return;

    // Promo-banner rules (baked JSON island from snippets/promo-banners.liquid)
    this._promoRules = [];
    try {
      const rulesEl = document.getElementById('PromoBannerRules');
      if (rulesEl) this._promoRules = JSON.parse(rulesEl.textContent) || [];
    } catch (e) { this._promoRules = []; }

    try {
      let products = [];
      try {
        const res = await fetch(
          '/recommendations/products.js?product_id=' + productId + '&limit=6&intent=related'
        );
        if (res.ok) {
          const data = await res.json();
          products = (data.products || []).slice(0, 6);
        }
      } catch (e) { /* related fetch is non-fatal — a pinned item may still render */ }

      // Guaranteed upsell (e.g. Beret when 2+ crochet items are in the cart). Liquid sets
      // data-pin-handle only when the rule applies; pin it to the front unless it's already
      // in the related set. Fetched via the product AJAX endpoint (same object shape).
      const pinHandle = wrap.dataset.pinHandle;
      if (pinHandle && !products.some(p => p && p.handle === pinHandle)) {
        try {
          const pr = await fetch('/products/' + pinHandle + '.js');
          if (pr.ok) {
            const pinned = await pr.json();
            if (pinned && pinned.id != null) products.unshift(pinned);
          }
        } catch (e) { /* pin is best-effort */ }
      }
      products = products.slice(0, 6);
      if (!products.length) return;

      // Keep the full product objects so the quick-add modal can read variants/options.
      this._recProducts = {};
      products.forEach(p => { if (p && p.id != null) this._recProducts[p.id] = p; });

      const grid = wrap.querySelector('[data-rec-grid]');
      if (!grid) return;
      grid.innerHTML = products.map(p => this.recCard(p)).join('');
      wrap.hidden = false;

      grid.querySelectorAll('[data-rec-add]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.variantId;
          if (!id) return;
          btn.disabled = true;
          const label = btn.textContent;
          btn.textContent = 'Adding…';
          try {
            // Flag pins so the free pin-back auto-adds (find the rec product by variant id).
            let recProd = null;
            const rp = this._recProducts || {};
            for (const k in rp) { if (rp[k] && (rp[k].variants || []).some(v => String(v.id) === String(id))) { recProd = rp[k]; break; } }
            const addBody = { id: id, quantity: 1 };
            if (isPinProduct(recProd)) addBody.properties = { _pin: '1' };
            // Recommendation products come from /recommendations/products.js, which —
            // like /products/{handle}.js — carries no inventory_quantity/inventory_policy
            // (confirmed elsewhere this session), so there's no client-side number to cap
            // against here. Fetch directly (not postCart's boolean-only wrapper) so a
            // server-side stock rejection's real description can be surfaced as the warning
            // — Shopify's own deny-oversell check is the authoritative source in this case.
            const response = await withCartLock(() => fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(addBody)
            }));
            if (!response.ok) {
              const err = await response.json().catch(() => ({}));
              throw new Error(err.description || 'Sorry, that item could not be added to your cart.');
            }
            // Refresh the cart in place so the recommendation card stays available.
            await this.refreshCartContents();
            const rc = await reconcileFreeBacks();
            if (rc.changed) await this.refreshCartContents();
            btn.textContent = 'Added ✓';
            setTimeout(() => { btn.disabled = false; btn.textContent = label; }, 1200);
          } catch (e) {
            btn.disabled = false;
            btn.textContent = label;
            showToast(e.message || "Couldn't add to cart.");
          }
        });
      });

      // Multi-variant items open the quick-add modal instead of leaving the page.
      grid.querySelectorAll('[data-rec-choose]').forEach(btn => {
        btn.addEventListener('click', () => {
          const prod = this._recProducts[btn.dataset.productId];
          if (prod) this.openQuickAdd(prod);
        });
      });
    } catch (err) {
      console.error('[CartPage] recommendations error:', err);
    }
  },

  // Resize a Shopify CDN image URL on the fly so the modal doesn't pull the
  // full-resolution original (recommendations JSON returns un-sized image URLs).
  cdnImg(src, w) {
    if (!src) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  },

  // Tag/price icon — same one used on the product page sale banners.
  tagIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
  },

  // Deal label + tag-targeted promo banners (mirrors product-card.liquid / the
  // product-page sale banners). Shipping promos are excluded — shipping is
  // irrelevant per-product here. Returns inner HTML (no wrapper).
  dealsHTML(p) {
    const tagIcon = this.tagIcon();
    const tags = (p.tags || []).map(t => String(t).toLowerCase());
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const hasDealTag = tags.some(t => t.includes('sale') || t.includes('deal') || t.includes('clearance'));
    let deal = '';
    if (onSale) {
      const pct = Math.floor((p.compare_at_price - p.price) * 100 / p.compare_at_price);
      deal = '<p class="product-card__deal-label">' + tagIcon + '<span>Save ' + pct + '% — Limited Time</span></p>';
    } else if (hasDealTag) {
      deal = '<p class="product-card__deal-label">' + tagIcon + '<span>Deal Running</span></p>';
    }
    const promos = (this._promoRules || [])
      .filter(r => !r.shipping && (!r.tag || tags.includes(String(r.tag).toLowerCase().trim())))
      .map(r => '<p class="cart-rec-card__promo cart-rec-card__promo--' + (r.style || 'purple') + '">' + tagIcon +
        '<span>' + String(r.message || '').replace(/</g, '&lt;') + '</span></p>')
      .join('');
    return deal + promos;
  },

  // Tag/type-triggered safety warnings — mirrors the product-page snippets
  // (ca-safety-warning, pins-safety-warning, small-parts-warning, crochet-disclaimer)
  // so the quick-add mini product page shows the same applicable warnings.
  warningsHTML(p) {
    const tags = (p.tags || []).map(t => String(t).toLowerCase().trim());
    const type = String(p.type || '').toLowerCase();
    const has = t => tags.indexOf(t) !== -1;

    const icon = '<svg class="safety-warning__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
    const sHeader = '<div class="safety-warning__header">' + icon + '<span class="safety-warning__title">Safety Information</span></div>';
    const sBox = body => '<div class="safety-warning" role="note" aria-label="Product Safety Information">' + sHeader + '<div class="safety-warning__body">' + body + '</div></div>';

    // Unique Disclaimer renders first, then the safety warnings below it.
    let disclaimer = '';
    let html = '';

    // California Prop 65 notices were removed sitewide 2026-07-10. The former
    // metal/warning CA box no longer renders; the metal/warning + metal-type
    // checks are kept only to preserve which items show the pins box below.
    const isMetalWarn = has('warning') || has('metal');

    // pins-safety-warning: small-parts / sharp-point notice (no Prop 65). Metal
    // items stay excluded so the quick-add card matches the prior product-page output.
    const isMetalType = type.indexOf('metal') !== -1;
    if (!isMetalWarn && !isMetalType) {
      const pinTags = ['enamel pin', 'enamel pins', 'enamel-pin', 'pin', 'pins', 'hard enamel', 'soft enamel'];
      const isPin = type.indexOf('pin') !== -1 || type.indexOf('enamel') !== -1 || pinTags.some(has);
      if (isPin) {
        html += sBox('<p>This enamel pin contains small parts and a functional sharp point. Keep out of reach of children under 3 years of age. Use care when attaching or removing the pin. Adult supervision is recommended for younger children.</p>');
      }
    }

    // small-parts-warning: choking hazard, ages 3+, no Prop 65.
    const ageTags = ['ages 3+', 'age 3+', 'ages 3 and up', 'age 3 and up', '3+', 'ages-3-plus', 'ages-3-and-up', 'small parts', 'small-parts', 'choking hazard'];
    if (ageTags.some(has)) {
      html += sBox('<p>Warning: Choking hazard — this product contains small parts. Not suitable for children under 3 years of age. Keep out of reach of children under 3. Adult supervision is recommended for younger children.</p>');
    }

    // crochet-disclaimer: handmade crochet (purple, its own classes).
    const crochetTags = ['crochet', 'crocheted', 'amigurumi'];
    if (type.indexOf('crochet') !== -1 || crochetTags.some(has)) {
      disclaimer = '<div class="unique-disclaimer" role="note" aria-label="Product Disclaimer">' +
        '<div class="unique-disclaimer__header">' +
          '<svg class="unique-disclaimer__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>' +
          '<span class="unique-disclaimer__title">Unique Disclaimer</span>' +
        '</div>' +
        '<div class="unique-disclaimer__body"><p>All crochet items are handmade with love, not machine manufactured. Please be aware that each item is not identical, but may have slight differences from the next. There may be more than one of the same item, but they are all one of a kind.</p></div>' +
      '</div>';
    }

    return disclaimer + html;
  },

  recCard(p) {
    const title = (p.title || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const img = p.featured_image
      ? '<div class="product-card__image-wrapper"><img src="' + p.featured_image + '" alt="' + title + '" loading="lazy"></div>'
      : '<div class="product-card__image-wrapper"><div class="product-card__placeholder">No Image</div></div>';
    const vendor = p.vendor
      ? '<p class="product-card__vendor">' + String(p.vendor).replace(/</g, '&lt;') + '</p>'
      : '';
    // ADD shares the CHECKOUT button's filled scheme (btn--primary).
    let action;
    if (p.variants && p.variants.length > 1) {
      // Needs a size/option choice — open a quick-add modal so the shopper stays on the cart page.
      action = '<button type="button" class="btn btn--primary btn--sm cart-rec-card__add" data-rec-choose data-product-id="' + p.id + '">Add</button>';
    } else if (p.variants && p.variants.length === 1 && p.variants[0].available) {
      action = '<button type="button" class="btn btn--primary btn--sm cart-rec-card__add" data-rec-add data-variant-id="' + p.variants[0].id + '">Add</button>';
    } else {
      action = '<a href="' + p.url + '" class="btn btn--ghost btn--sm cart-rec-card__add">View</a>';
    }
    // Deal label + promo banners (shared with the quick-add modal).
    const inner = this.dealsHTML(p);
    const deals = inner ? '<div class="cart-rec-card__deals">' + inner + '</div>' : '';
    return '' +
      '<div class="product-card product-card--rec">' +
        '<a href="' + p.url + '" class="product-card__image-link" aria-label="' + title + '">' + img + '</a>' +
        '<div class="product-card__info">' +
          vendor +
          '<h3 class="product-card__title"><a href="' + p.url + '">' + title + '</a></h3>' +
          '<div class="product-card__price"><span class="price">' + formatMoney(p.price) + '</span></div>' +
          '<div class="cart-rec-card__footer">' + deals + action + '</div>' +
        '</div>' +
      '</div>';
  },

  /* ---- Quick-add modal (cart recommendations that need an option choice) ---- */
  ensureQuickAddModal() {
    if (this._qaModal) return this._qaModal;
    const modal = document.createElement('div');
    modal.className = 'qa-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML =
      '<div class="qa-modal__overlay" data-qa-close></div>' +
      '<div class="qa-modal__dialog" role="document">' +
        '<button type="button" class="qa-modal__close" data-qa-close aria-label="Close">&times;</button>' +
        '<div class="qa-modal__body">' +
          '<div class="qa-modal__media">' +
            '<a class="qa-modal__image-link" data-qa-link>' +
              '<img class="qa-modal__image" data-qa-image alt="" loading="lazy">' +
            '</a>' +
            '<div class="qa-modal__thumbs" data-qa-thumbs></div>' +
          '</div>' +
          '<div class="qa-modal__details">' +
            '<p class="qa-modal__vendor" data-qa-vendor></p>' +
            '<h2 class="qa-modal__title" data-qa-title></h2>' +
            '<p class="qa-modal__price" data-qa-price></p>' +
            '<div class="qa-modal__promos" data-qa-promos></div>' +
            '<div class="qa-modal__options" data-qa-options></div>' +
            '<p class="qa-modal__error" data-qa-error hidden></p>' +
            '<div class="qa-modal__buy">' +
              '<div class="quantity-selector qa-modal__qty">' +
                '<button type="button" class="qty-btn" data-qa-qty-minus aria-label="Decrease quantity">' +
                  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
                '</button>' +
                '<input type="number" class="qty-input" value="1" min="1" data-qa-qty aria-label="Quantity">' +
                '<button type="button" class="qty-btn" data-qa-qty-plus aria-label="Increase quantity">' +
                  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
                '</button>' +
              '</div>' +
              '<button type="button" class="btn btn--primary qa-modal__add" data-qa-add disabled>Add to Cart</button>' +
            '</div>' +
            '<a class="qa-modal__full-link" data-qa-link>View full product details</a>' +
            '<div class="qa-modal__desc" data-qa-desc></div>' +
            '<div class="qa-modal__warnings" data-qa-warnings></div>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    modal.querySelectorAll('[data-qa-close]').forEach(el => el.addEventListener('click', () => this.closeQuickAdd()));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) this.closeQuickAdd();
    });
    modal.querySelector('[data-qa-add]').addEventListener('click', () => this.quickAddSubmit());
    // Quantity stepper.
    const qtyInput = modal.querySelector('[data-qa-qty]');
    modal.querySelector('[data-qa-qty-minus]').addEventListener('click', () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
    });
    modal.querySelector('[data-qa-qty-plus]').addEventListener('click', () => {
      const current = parseInt(qtyInput.value, 10) || 1;
      const variant = this._qaVariant;
      const cap = variant ? this._qaInventory[String(variant.id)] : null;
      if (cap != null && current >= cap) {
        showToast(cap > 0 ? `Only ${cap} available in stock.` : 'Out of stock.');
        return;
      }
      qtyInput.value = current + 1;
    });
    this._qaModal = modal;
    return modal;
  },

  openQuickAdd(product, onAdded, inventoryMap) {
    const modal = this.ensureQuickAddModal();
    this._qaProduct = product;
    this._qaVariant = null;
    // Per-variant stock caps (variantId -> qty), when known — see chooseOptions().
    this._qaInventory = inventoryMap || {};
    // Grid cards pass onAdded (open the cart drawer after the add); the cart page
    // passes none and refreshes its recommendations in place. See quickAddSubmit.
    this._qaOnAdded = onAdded || null;
    const optionNames = (product.options || []).map(o => (o && o.name) ? o.name : o);
    this._qaSelected = optionNames.map(() => null);

    modal.querySelector('[data-qa-vendor]').textContent = product.vendor || '';
    modal.querySelector('[data-qa-title]').textContent = product.title || '';
    modal.querySelector('[data-qa-price]').textContent = formatMoney(product.price);
    modal.querySelector('[data-qa-promos]').innerHTML = this.dealsHTML(product);
    modal.querySelector('[data-qa-qty]').value = 1;

    // Product image + gallery thumbnails (condensed product page).
    const images = (product.images && product.images.length)
      ? product.images
      : (product.featured_image ? [product.featured_image] : []);
    const mainImg = modal.querySelector('[data-qa-image]');
    const thumbs = modal.querySelector('[data-qa-thumbs]');
    if (images.length) {
      mainImg.src = this.cdnImg(images[0], 800);
      mainImg.alt = product.title || '';
      mainImg.hidden = false;
      thumbs.innerHTML = images.length > 1
        ? images.map((src, i) =>
            '<button type="button" class="qa-modal__thumb' + (i === 0 ? ' is-active' : '') +
            '" data-qa-thumb><img src="' + this.cdnImg(src, 120) + '" alt="" loading="lazy"></button>'
          ).join('')
        : '';
      thumbs.querySelectorAll('[data-qa-thumb]').forEach((b, i) => {
        b.addEventListener('click', () => {
          mainImg.src = this.cdnImg(images[i], 800);
          thumbs.querySelectorAll('[data-qa-thumb]').forEach(x => x.classList.toggle('is-active', x === b));
        });
      });
    } else {
      mainImg.hidden = true;
      thumbs.innerHTML = '';
    }

    // Links to the full product page.
    modal.querySelectorAll('[data-qa-link]').forEach(a => { a.href = product.url || '#'; });

    // Description (rich HTML from the product, as shown on the product page).
    const desc = modal.querySelector('[data-qa-desc]');
    desc.innerHTML = product.description || '';

    // Applicable safety warnings (same triggers as the product page).
    modal.querySelector('[data-qa-warnings]').innerHTML = this.warningsHTML(product);

    const optWrap = modal.querySelector('[data-qa-options]');
    optWrap.innerHTML = optionNames.map((name, i) => {
      const seen = [];
      product.variants.forEach(v => {
        const val = v['option' + (i + 1)];
        if (val != null && seen.indexOf(val) === -1) seen.push(val);
      });
      const buttons = seen.map(val =>
        '<button type="button" class="qa-modal__value" data-qa-opt="' + i + '" data-qa-val="' +
        String(val).replace(/"/g, '&quot;') + '">' + String(val).replace(/</g, '&lt;') + '</button>'
      ).join('');
      return '<div class="qa-modal__option"><span class="qa-modal__option-name">' +
        String(name).replace(/</g, '&lt;') + '</span><div class="qa-modal__values">' + buttons + '</div></div>';
    }).join('');

    optWrap.querySelectorAll('[data-qa-val]').forEach(b => {
      b.addEventListener('click', () => {
        const i = parseInt(b.dataset.qaOpt, 10);
        this._qaSelected[i] = b.dataset.qaVal;
        optWrap.querySelectorAll('[data-qa-opt="' + i + '"]').forEach(x => x.classList.toggle('is-active', x === b));
        this.quickAddResolve();
      });
    });

    this.quickAddResolve();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
  },

  quickAddResolve() {
    const modal = this._qaModal, product = this._qaProduct;
    if (!modal || !product) return;
    const sel = this._qaSelected;

    // Disable any option VALUE (e.g. a size) that can't resolve to an available
    // variant given the OTHER currently-chosen options — not just gray out the
    // final Add button afterward. A value is dead if every variant matching it
    // (crossed with the rest of the current selection) is sold out, or none exist.
    const optWrap = modal.querySelector('[data-qa-options]');
    if (optWrap) {
      optWrap.querySelectorAll('[data-qa-val]').forEach(b => {
        const i = parseInt(b.dataset.qaOpt, 10);
        const val = b.dataset.qaVal;
        const matches = product.variants.filter(v => {
          if (String(v['option' + (i + 1)]) !== String(val)) return false;
          return sel.every((s, j) => j === i || s == null || String(v['option' + (j + 1)]) === String(s));
        });
        const dead = matches.length === 0 || matches.every(v => !v.available);
        b.disabled = dead;
      });

      // Auto-select an option that has exactly one live (non-dead) value — a
      // single in-stock size was showing its default focus ring on modal open,
      // which reads as "already picked," but nothing was actually selected and
      // Add to Cart stayed disabled until the shopper clicked it again themselves.
      optWrap.querySelectorAll('.qa-modal__option').forEach(optEl => {
        const buttons = Array.from(optEl.querySelectorAll('[data-qa-val]'));
        if (!buttons.length) return;
        const i = parseInt(buttons[0].dataset.qaOpt, 10);
        if (sel[i] != null) return;
        const live = buttons.filter(b => !b.disabled);
        if (live.length === 1) {
          sel[i] = live[0].dataset.qaVal;
          buttons.forEach(x => x.classList.toggle('is-active', x === live[0]));
        }
      });
    }

    const allChosen = sel.every(s => s != null);
    let variant = null;
    if (allChosen) {
      variant = product.variants.find(v => sel.every((s, i) => String(v['option' + (i + 1)]) === String(s)));
    }
    this._qaVariant = variant;

    const addBtn = modal.querySelector('[data-qa-add]');
    const price = modal.querySelector('[data-qa-price]');
    const qtyInput = modal.querySelector('[data-qa-qty]');
    if (!allChosen) {
      addBtn.disabled = true; addBtn.textContent = 'Select a Size';
    } else if (!variant) {
      addBtn.disabled = true; addBtn.textContent = 'Unavailable';
    } else if (!variant.available) {
      addBtn.disabled = true; addBtn.textContent = 'Sold Out';
    } else {
      addBtn.disabled = false; addBtn.textContent = 'Add to Cart';
      if (variant.price != null) price.textContent = formatMoney(variant.price);
    }

    // Re-cap the quantity input to this variant's own stock (switching size can
    // change the cap) — clamp silently here; the +/- handlers below alert when a
    // click is actually blocked by it.
    if (qtyInput) {
      const cap = variant ? this._qaInventory[String(variant.id)] : null;
      if (cap != null) {
        qtyInput.max = cap;
        const cur = parseInt(qtyInput.value, 10) || 1;
        if (cur > cap) qtyInput.value = Math.max(1, cap);
      } else {
        qtyInput.removeAttribute('max');
      }
    }
  },

  async quickAddSubmit() {
    const modal = this._qaModal, variant = this._qaVariant;
    if (!variant || !variant.available) return;
    const addBtn = modal.querySelector('[data-qa-add]');
    const err = modal.querySelector('[data-qa-error]');
    const qty = Math.max(1, parseInt(modal.querySelector('[data-qa-qty]').value, 10) || 1);

    // Authoritative stock guard (same shape as the PDP's), counting whatever of
    // this variant is already in the cart — the qty input's own max only guards
    // against typing past it, not a stale cap from before an earlier add.
    const cap = this._qaInventory[String(variant.id)];
    if (cap != null) {
      const cart = await fetchCartJson();
      let inCart = 0;
      if (cart && cart.items) cart.items.forEach(i => { if (String(i.variant_id) === String(variant.id)) inCart += i.quantity; });
      const remaining = cap - inCart;
      if (qty > remaining) {
        showToast(
          remaining <= 0
            ? (cap <= 0 ? 'Out of stock.' : 'Max stock already added.')
            : `Only ${remaining} more available.`
        );
        return;
      }
    }

    err.hidden = true;
    addBtn.disabled = true; addBtn.textContent = 'Adding…';
    try {
      const addBody = { id: variant.id, quantity: qty };
      const props = {};
      if (isPinProduct(this._qaProduct)) props._pin = '1'; // flag pins for the free back
      if (cap != null) props._stockcap = String(cap); // see variantStockCap() in the cart section
      if (Object.keys(props).length) addBody.properties = props;
      // Fetch directly (not postCart's boolean-only wrapper): when this modal was
      // opened from the recommendations carousel (no inventoryMap, so `cap` above is
      // null and the guard above was skipped), a server-side stock rejection's real
      // description is the only warning available — surface it instead of a generic one.
      const response = await withCartLock(() => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addBody)
      }));
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.description || 'Sorry, that item could not be added.');
      }
      if (this._qaOnAdded) {
        // Grid-card path: close the mini product page and open the cart drawer, which
        // renders + reconciles the free pin-back. No cart page to refresh in place.
        const cb = this._qaOnAdded;
        this.closeQuickAdd();
        cb();
      } else {
        // Cart-page path: refresh in place (no full reload) so the recommendation card
        // stays under "Conjure a Little More" — the shopper can reopen it for another size.
        await this.refreshCartContents();
        const rc = await reconcileFreeBacks();
        if (rc.changed) await this.refreshCartContents();
        this.closeQuickAdd();
      }
    } catch (e) {
      addBtn.disabled = false; addBtn.textContent = 'Add to Cart';
      err.textContent = e.message || 'Sorry, that item could not be added.';
      err.hidden = false;
    }
  },

  closeQuickAdd() {
    if (!this._qaModal) return;
    this._qaModal.classList.remove('is-open');
    this._qaModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  }
};

/* ============================================================
   12. SMOOTH SECTION ANIMATIONS (Intersection Observer)
   ============================================================ */

const SectionAnimations = {
  init() {
    if (!('IntersectionObserver' in window)) return;

    const style = document.createElement('style');
    style.textContent = `
      .fade-in-up {
        opacity: 0;
        transform: translateY(24px);
        transition: opacity 0.6s ease, transform 0.6s ease;
      }
      .fade-in-up.is-visible {
        opacity: 1;
        transform: translateY(0);
      }
    `;
    document.head.appendChild(style);

    const elements = document.querySelectorAll(
      '.product-card, .collection-card, .article-card, .section-heading'
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            // Stagger delay for grid items
            const siblings = Array.from(entry.target.parentNode.children);
            const index = siblings.indexOf(entry.target);
            const delay = Math.min(index * 80, 400);
            setTimeout(() => {
              entry.target.classList.add('is-visible');
            }, delay);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    // Skip elements already in the initial viewport — fading above-the-fold content
    // (near the LCP) just flashes it in and delays perceived load. Only animate
    // cards the user has to scroll to reveal.
    const vh = window.innerHeight;
    elements.forEach(el => {
      if (el.getBoundingClientRect().top < vh) return;
      el.classList.add('fade-in-up');
      observer.observe(el);
    });
  }
};

/* ============================================================
   CATEGORY IMAGE ROTATOR (Shop by Category)
   ============================================================ */

const CategoryRotator = {
  init(root) {
    const scope = root || document;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scope.querySelectorAll('[data-category-rotator]').forEach((rotator, index) => {
      // Avoid starting a second timer on a card that's already rotating.
      if (rotator.dataset.rotating === 'true') return;

      let urls = [];
      try { urls = JSON.parse(rotator.dataset.rotatorImages || '[]'); } catch (e) { urls = []; }
      if (urls.length <= 1) return; // nothing to rotate

      rotator.dataset.rotating = 'true';

      const alt = (rotator.querySelector('.collection-card__img') || {}).alt || '';

      // Two stacked layers we cross-fade between, so only the image currently
      // being shown is ever downloaded — chosen at random from the full list.
      rotator.innerHTML = '';
      const layers = [document.createElement('img'), document.createElement('img')];
      layers.forEach(img => {
        img.className = 'collection-card__img';
        img.alt = alt;
        img.loading = 'lazy';
        rotator.appendChild(img);
      });

      let activeLayer = 0;
      let lastIndex = -1;

      const pick = () => {
        let i = lastIndex;
        while (i === lastIndex) i = Math.floor(Math.random() * urls.length);
        lastIndex = i;
        return i;
      };

      const show = (initial) => {
        const incoming = layers[initial ? activeLayer : 1 - activeLayer];
        incoming.onload = () => {
          if (!initial) {
            layers[activeLayer].classList.remove('is-active');
            activeLayer = 1 - activeLayer;
          }
          incoming.classList.add('is-active');
        };
        incoming.src = urls[pick()];
      };

      show(true);
      // Reduced-motion: render one static image, no cross-fade cycle.
      if (reduceMotion) return;
      // Stagger each card so they don't all cross-fade at the same instant —
      // each one's cycle starts a bit later than the previous card's.
      const interval = 3500;
      const stagger = 900;
      setTimeout(() => {
        show(false);
        setInterval(() => show(false), interval);
      }, 400 + index * stagger);
    });
  }
};

// Re-initialize when a section is (re)rendered in the Shopify Theme Editor,
// e.g. after changing which collections are shown in Shop by Category.
document.addEventListener('shopify:section:load', (e) => {
  CategoryRotator.init(e.target);
});

/* ============================================================
   INITIALIZATION
   ============================================================ */

/* "You May Also Like" carousel — arrows scroll the track; hide arrows at the ends */
const RelatedCarousel = {
  init() {
    document.querySelectorAll('[data-rel-carousel]').forEach(car => {
      const track = car.querySelector('[data-rel-track]');
      const prev = car.querySelector('[data-rel-prev]');
      const next = car.querySelector('[data-rel-next]');
      if (!track) return;
      const step = () => Math.max(track.clientWidth * 0.85, 200);
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        const maxScroll = track.scrollWidth - track.clientWidth - 2;
        const scrollable = maxScroll > 0;
        if (prev) prev.style.display = (scrollable && track.scrollLeft > 2) ? 'flex' : 'none';
        if (next) next.style.display = (scrollable && track.scrollLeft < maxScroll) ? 'flex' : 'none';
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }
};

/* Checkout warnings gate — Made-to-Order AND Marked-Down Misfit items.
   On Checkout click we re-fetch the mto-gate section (Section Rendering API) so the
   warning flags and the live Misfit item list reflect the CURRENT cart (after AJAX
   adds/removes), swap in the fresh modal, and show it if either warning applies. */
const MtoGate = {
  init() {
    if (!document.querySelector('[data-mto-modal]')) return;
    this.pendingUrl = null;
    this.busy = false;
    this.bind(document.querySelector('[data-mto-modal]'));

    // Intercept checkout links (cart page + cart drawer), capture phase
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href="/checkout"], a[href$="/checkout"]');
      if (!link) return;
      e.preventDefault();
      this.handleCheckout(link.href);
    }, true);

    document.addEventListener('keydown', (e) => {
      const modal = document.querySelector('[data-mto-modal]');
      if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) this.close();
    });
  },

  bind(modal) {
    if (!modal) return;
    modal.querySelectorAll('[data-mto-cancel]').forEach(el =>
      el.addEventListener('click', () => this.close()));
    // Each warning is its own step with its own agree button; agree advances to the
    // next step, or proceeds to checkout once the last step is acknowledged.
    modal.querySelectorAll('[data-mto-agree]').forEach(el =>
      el.addEventListener('click', () => this.advance()));
  },

  advance() {
    const modal = document.querySelector('[data-mto-modal]');
    if (!modal) return;
    const steps = Array.from(modal.querySelectorAll('[data-mto-step]'));
    const i = steps.findIndex(s => !s.hidden);
    if (i > -1 && i < steps.length - 1) {
      steps[i].hidden = true;
      const next = steps[i + 1];
      next.hidden = false;
      const dialog = modal.querySelector('.mto-modal__dialog');
      if (dialog) dialog.scrollTop = 0;
      const a = next.querySelector('[data-mto-agree]');
      if (a) a.focus();
    } else if (this.pendingUrl) {
      window.location.href = this.pendingUrl;
    }
  },

  async handleCheckout(url) {
    if (this.busy) return;
    this.busy = true;
    // Re-fetch the section so flags + the Misfit item list reflect the CURRENT cart.
    let fresh = null;
    try {
      const res = await fetch('/?sections=mto-gate', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (res.ok) {
        const data = await res.json();
        const tmp = document.createElement('div');
        tmp.innerHTML = data['mto-gate'] || '';
        fresh = tmp.querySelector('[data-mto-modal]');
      }
    } catch (e) { /* fall back to the modal already in the DOM */ }
    this.busy = false;

    const current = document.querySelector('[data-mto-modal]');
    if (fresh && current && current.parentNode) {
      current.parentNode.replaceChild(fresh, current);
      this.bind(fresh);
    }
    const modal = document.querySelector('[data-mto-modal]');
    const warn = modal && (modal.dataset.cartHasMto === 'true' || modal.dataset.cartHasMisfit === 'true');
    if (warn) { this.pendingUrl = url; this.open(); }
    else { window.location.href = url; }
  },

  open() {
    const modal = document.querySelector('[data-mto-modal]');
    if (!modal) return;
    // Always start at the first step (the modal may be reopened after a Go Back).
    const steps = modal.querySelectorAll('[data-mto-step]');
    steps.forEach((s, i) => { s.hidden = i !== 0; });
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    const firstAgree = steps[0] && steps[0].querySelector('[data-mto-agree]');
    if (firstAgree) firstAgree.focus();
  },

  close() {
    const modal = document.querySelector('[data-mto-modal]');
    if (modal) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('no-scroll');
    this.pendingUrl = null;
  }
};

/* ============================================================
   BUILD-YOUR-OWN BUILDER (necklace / plug / jewelry)
   Pick one base part + 1..N add-ons, then add every chosen part as its own cart
   line stamped with a shared `_build` id so the drawer groups them into one piece.
   Parts are real "builder-part" products, so stock is real and overselling can't
   happen — the server rejects the whole add (422) if any part just sold out.
   ============================================================ */
const ProductBuilder = {
  init() {
    this.root = document.querySelector('[data-product-builder]');
    if (!this.root) return;
    this.buildLabel = this.root.dataset.buildLabel || 'Custom build';
    this.min = parseInt(this.root.dataset.addonMin, 10) || 0;
    this.max = parseInt(this.root.dataset.addonMax, 10) || 99;

    this.baseInputs = Array.from(this.root.querySelectorAll('[data-builder-part="base"]'));
    this.addonInputs = Array.from(this.root.querySelectorAll('[data-builder-part="addon"]'));
    // Remember which add-ons are disabled because they're SOLD OUT (vs. temporarily
    // locked because the max is reached) so we never re-enable a sold-out option.
    this.addonInputs.forEach(i => { i._soldout = i.disabled; });

    this.summary = this.root.querySelector('[data-builder-chosen]');
    this.totalEl = this.root.querySelector('[data-builder-total]');
    this.hintEl = this.root.querySelector('[data-builder-hint]');
    this.addBtn = this.root.querySelector('[data-builder-add]');
    this.addBtnText = this.root.querySelector('[data-builder-add-text]');

    this.baseInputs.forEach(i => i.addEventListener('change', () => this.update()));
    this.addonInputs.forEach(i => i.addEventListener('change', () => this.update()));
    if (this.addBtn) this.addBtn.addEventListener('click', () => this.submit());

    this.update();
  },

  selectedBase() { return this.baseInputs.find(i => i.checked) || null; },
  selectedAddons() { return this.addonInputs.filter(i => i.checked); },

  update() {
    const base = this.selectedBase();
    const addons = this.selectedAddons();

    // Lock remaining add-ons once the max is reached; release them below the max.
    if (addons.length >= this.max) {
      this.addonInputs.forEach(i => { if (!i.checked && !i._soldout) i.disabled = true; });
    } else {
      this.addonInputs.forEach(i => { if (!i._soldout) i.disabled = false; });
    }

    let total = 0;
    let rows = '';
    if (base) { total += parseInt(base.dataset.price, 10) || 0; rows += this.rowHTML(base); }
    addons.forEach(a => { total += parseInt(a.dataset.price, 10) || 0; rows += this.rowHTML(a); });

    if (this.summary) {
      this.summary.innerHTML = (!base && !addons.length)
        ? '<p class="builder-summary__placeholder">Start by choosing a base above.</p>'
        : rows;
    }
    if (this.totalEl) this.totalEl.textContent = formatMoney(total);

    const ready = !!base && addons.length >= this.min;
    if (this.addBtn) this.addBtn.disabled = !ready;
    if (this.hintEl) {
      let hint = '';
      if (!base) hint = 'Choose a base to begin.';
      else if (addons.length < this.min) {
        const need = this.min - addons.length;
        hint = `Add ${need} more ${need === 1 ? 'piece' : 'pieces'} to continue.`;
      } else if (addons.length >= this.max) hint = `Maximum ${this.max} reached.`;
      this.hintEl.textContent = hint;
    }
  },

  rowHTML(input) {
    const img = input.dataset.image ? `<img src="${input.dataset.image}" alt="" loading="lazy">` : '';
    const price = parseInt(input.dataset.price, 10) || 0;
    // dataset.title is already HTML-escaped in Liquid (| escape).
    return `
      <div class="builder-chosen">
        <div class="builder-chosen__img">${img}</div>
        <span class="builder-chosen__name">${input.dataset.title || ''}</span>
        <span class="builder-chosen__price">${formatMoney(price)}</span>
      </div>`;
  },

  props(buildId, role) {
    return {
      _build: buildId,
      _build_role: role,
      _build_label: this.buildLabel,
      'Custom build': this.buildLabel
    };
  },

  async submit() {
    const base = this.selectedBase();
    const addons = this.selectedAddons();
    if (!base || addons.length < this.min) return;

    const buildId = 'b' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
    const items = [{ id: base.value, quantity: 1, properties: this.props(buildId, 'base') }];
    addons.forEach(a => items.push({ id: a.value, quantity: 1, properties: this.props(buildId, 'addon') }));

    if (this.addBtn) this.addBtn.disabled = true;
    const origText = this.addBtnText ? this.addBtnText.textContent : '';
    if (this.addBtnText) this.addBtnText.textContent = 'Adding…';

    try {
      // Open the drawer instantly (loading state) before the network add resolves.
      if (window.cartDrawer) window.cartDrawer.openDrawerLoading();
      const res = await withCartLock(() => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      }));
      if (!res.ok) {
        // The whole add is atomic — if any part just sold out, nothing was added.
        let msg = 'A piece just sold out. Refreshing…';
        try { const e = await res.json(); if (e && e.description) msg = e.description; } catch (_) {}
        showToast(msg);
        setTimeout(() => window.location.reload(), 1600); // give the toast time to be read before reload wipes it
        return;
      }
      // Reset for the next build, then open the drawer to show the grouped piece.
      this.baseInputs.forEach(i => { i.checked = false; });
      this.addonInputs.forEach(i => { i.checked = false; });
      this.update();
      if (window.cartDrawer) window.cartDrawer.openDrawer();
    } catch (err) {
      console.error('[ProductBuilder] add error:', err);
      showToast("Couldn't add your build. Try again.");
      if (window.cartDrawer) window.cartDrawer.renderCartItems(); // clear the skeleton
    } finally {
      if (this.addBtnText) this.addBtnText.textContent = origText;
      this.update();
    }
  }
};

// Card image load-in: marks each product-card image box .is-loaded once its photo
// has painted, which retires the loading shimmer. Idempotent and scoped, so it can
// be re-run over freshly swapped grids (e.g. after collection filtering).
const CardMediaLoad = {
  init(root = document) {
    root.querySelectorAll('.product-card__image-wrapper:not([data-media-bound])').forEach((wrap) => {
      wrap.setAttribute('data-media-bound', '');
      const img = wrap.querySelector('img');
      if (!img) { wrap.classList.add('is-loaded'); return; }
      if (img.complete && img.naturalWidth > 0) {
        wrap.classList.add('is-loaded');
      } else {
        const done = () => wrap.classList.add('is-loaded');
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }
};

/* ============================================================
   CARD QUICK-ADD — add to cart straight from product-grid cards (Option 01:
   the "+ Add" outline pill). Single variant (pins, plush, socks, clips, bags) =
   one-tap add -> cart drawer. Multi variant (sized apparel) = "Select size" ->
   inline size chips revealed in the card; a chip adds that variant (never blind-
   adds a default). Event-delegated so AJAX-filtered grids keep working. Pins are
   stamped _pin so the free locking back reconciles (via the drawer render); the
   "4 for $20" Function applies from cart contents. One-of-a-kind items are capped
   at tracked stock — guarded at click time (authoritative) + a visual disable.
   ============================================================ */
const CardQuickAdd = {
  init() {
    // Polite live region so the "added" state is announced to screen readers.
    this.live = document.createElement('div');
    this.live.setAttribute('aria-live', 'polite');
    this.live.className = 'visually-hidden';
    document.body.appendChild(this.live);

    document.addEventListener('click', (e) => {
      const chooseBtn = e.target.closest('[data-card-choose]');
      if (chooseBtn) { e.preventDefault(); this.chooseOptions(chooseBtn); return; }
      const addBtn = e.target.closest('[data-card-add]');
      if (addBtn) { e.preventDefault(); this.oneTapAdd(addBtn); }
    });
    this.syncButtons();
  },

  announce(msg) { if (this.live) this.live.textContent = msg; },

  // Add a variant to the cart (shared by one-tap + size chips), then open the drawer
  // — which reconciles the free pin-back. Throws on failure.
  async addVariant(variantId, isPin, stockCap) {
    const body = { id: variantId, quantity: 1 };
    const props = {};
    if (isPin) props._pin = '1'; // flag pins for the free back
    // Stamp the known stock cap (from the card's server-rendered data-max-qty)
    // onto the line so the drawer's quantity stepper enforces it later even when
    // reopened from a page that never had window.productInventory (see
    // variantStockCap() in the cart section — this is the only reliable source).
    if (stockCap != null) props._stockcap = String(stockCap);
    if (Object.keys(props).length) body.properties = props;
    // Open the drawer instantly (loading state) so the tap feels immediate, before the
    // add + cart-fetch round-trips resolve.
    if (window.cartDrawer) window.cartDrawer.openDrawerLoading();
    try {
      const ok = await withCartLock(() => postCart('/cart/add.js', body));
      if (!ok) throw new Error('add failed');
    } catch (e) {
      if (window.cartDrawer) window.cartDrawer.renderCartItems(); // clear the skeleton
      throw e;
    }
    if (window.cartDrawer) window.cartDrawer.openDrawer(); // renders + reconciles free backs + updates counts
    this.announce('Added to cart');
    return true;
  },

  // ---- Single-variant "+ Add" pill: one tap adds qty 1 ----
  async oneTapAdd(btn) {
    if (btn.classList.contains('is-loading') || btn.classList.contains('is-added') || btn.classList.contains('is-maxed')) return;
    const id = btn.dataset.variantId;
    if (!id) return;
    const max = (btn.dataset.maxQty != null && btn.dataset.maxQty !== '') ? parseInt(btn.dataset.maxQty, 10) : null;

    // One-of-a-kind guard (authoritative): never exceed tracked stock, counting
    // units already in the cart, so a "Last one!" item can't be over-added.
    if (max != null) {
      const cart = await fetchCartJson();
      let inCart = 0;
      if (cart && cart.items) cart.items.forEach(i => { if (String(i.variant_id) === String(id)) inCart += i.quantity; });
      if (inCart >= max) {
        this.markMaxed(btn);
        showToast(max > 0 ? 'Max stock already added.' : 'Out of stock.');
        return;
      }
    }

    const orig = btn.dataset.origHtml || btn.innerHTML;
    btn.dataset.origHtml = orig;
    btn.classList.add('is-loading');
    btn.innerHTML = '<span class="product-card__qa-spin" aria-hidden="true"></span>';
    try {
      await this.addVariant(id, btn.dataset.pin === '1', max);
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.innerHTML = btn.dataset.origHtml;
        this.syncButtons();
      }, 1300);
    } catch (e) {
      btn.classList.remove('is-loading');
      btn.innerHTML = btn.dataset.origHtml;
      showToast("Couldn't add to cart.");
    }
  },

  // ---- Multi-variant: open the pop-in mini product page (shared quick-add modal),
  //      so a size is chosen there. Cleaner than inline chips (user preference). ----
  async chooseOptions(btn) {
    if (btn.classList.contains('is-loading')) return;
    const handle = btn.dataset.handle;
    if (!handle) return;
    const orig = btn.dataset.origHtml || btn.innerHTML;
    btn.dataset.origHtml = orig;
    btn.classList.add('is-loading');
    btn.innerHTML = '<span class="product-card__qa-spin" aria-hidden="true"></span>';
    try {
      const r = await fetch('/products/' + handle + '.js');
      if (!r.ok) throw new Error('product ' + r.status);
      const product = await r.json();
      btn.classList.remove('is-loading');
      btn.innerHTML = btn.dataset.origHtml;
      // Per-variant stock caps, read off the button's own data-inv-{id} attributes
      // (see card-quick-add.liquid) — the fetched product JSON above has no
      // inventory_quantity/inventory_policy at all, so this is the only source.
      const inv = {};
      Array.prototype.forEach.call(btn.attributes, (a) => {
        if (a.name.indexOf('data-inv-') === 0) inv[a.name.slice(9)] = parseInt(a.value, 10);
      });
      // Mini product page; after a successful add it opens the cart drawer.
      CartPage.openQuickAdd(product, () => { if (window.cartDrawer) window.cartDrawer.openDrawer(); }, inv);
    } catch (e) {
      btn.classList.remove('is-loading');
      btn.innerHTML = btn.dataset.origHtml;
      window.location.href = '/products/' + handle; // graceful fallback to the PDP
    }
  },

  markMaxed(btn) {
    btn.classList.add('is-maxed');
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('title', 'Already in your cart — this is the last one');
  },

  unmarkMaxed(btn) {
    if (!btn.classList.contains('is-maxed')) return;
    btn.classList.remove('is-maxed');
    btn.removeAttribute('aria-disabled');
    btn.setAttribute('title', 'Add to cart');
  },

  // Visually cap one-of-a-kind buttons already maxed in the cart. Correctness is
  // still enforced at click time in oneTapAdd; this is only the visual cue.
  async syncButtons() {
    const btns = document.querySelectorAll('[data-card-add][data-max-qty]');
    if (!btns.length) return;
    const cart = await fetchCartJson();
    const counts = {};
    if (cart && cart.items) cart.items.forEach(i => { counts[i.variant_id] = (counts[i.variant_id] || 0) + i.quantity; });
    btns.forEach(btn => {
      const inCart = counts[btn.dataset.variantId] || 0;
      const max = parseInt(btn.dataset.maxQty, 10);
      if (!isNaN(max) && inCart >= max) this.markMaxed(btn); else this.unmarkMaxed(btn);
    });
  }
};

document.addEventListener('DOMContentLoaded', () => {
  // Core UI
  AnnouncementBar.init();
  MegaMenu.init();
  MobileMenu.init();
  SearchBar.init();

  // Cart
  window.cartDrawer = new CartDrawer();
  MtoGate.init();

  // Product page
  QuantitySelector.init();
  ProductGallery.init();
  VariantPicker.init();
  RelatedCarousel.init();

  // Collection page
  CollectionFilters.init();

  // Build-your-own builder page
  ProductBuilder.init();

  // Cart page
  CartPage.init();

  // Animations
  SectionAnimations.init();

  // Shop by Category image rotator
  CategoryRotator.init();

  // Retire the product-card loading shimmer once each photo paints
  CardMediaLoad.init();

  // Quick-add to cart from product-grid cards
  CardQuickAdd.init();

  // Reconcile cart count (in case the page was served from full-page cache with a
  // stale server-rendered count). Deferred to idle so it never competes with first
  // paint / interaction — the count is already correct server-side on a fresh load.
  const refreshCartCount = () => {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.setAttribute('data-count', cart.item_count);
        });
      })
      .catch(() => {});
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(refreshCartCount, { timeout: 2000 });
  } else {
    setTimeout(refreshCartCount, 1000);
  }
});
